import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { fetchFileChunk, fetchFileComplete, fetchFileStreamed, fetchFileProperties, apiDeleteFile, getAuthUser, getAuthUserId, findUserByEmail, createFileShare, apiGetLocations, getFileLabel, setFileLabel } from '../services/api';
import { downloadFlacAsBw64, pickBw64SaveFile, isBw64ExportSupported, bw64UnsupportedReason } from '../utils/flacToBw64';

// Parse the JSON-Lines breadcrumb payload (one {"t","lat","lon","alt"} per line)
// into an array of {lat, lon} points, skipping malformed/partial lines.
const parseBreadcrumbs = (text) => {
    if (!text || typeof text !== 'string') return [];
    const points = [];
    for (const rawLine of text.split('\n')) {
        const line = rawLine.trim();
        if (!line) continue;
        try {
            const obj = JSON.parse(line);
            const lat = Number(obj.lat);
            const lon = Number(obj.lon);
            if (Number.isFinite(lat) && Number.isFinite(lon)) points.push({ lat, lon });
        } catch {
            // Ignore a half-written tail line from a live upload.
        }
    }
    return points;
};

// Build a Google Maps URL that renders the breadcrumb track. Google Maps has no
// URL scheme for an arbitrary polyline, so we use the Directions API URL, which
// draws a (road-snapped) route through the points. The interactive directions UI
// accepts only a handful of stops, so long tracks are downsampled evenly while
// always keeping the true start and end.
const buildGoogleMapsTrackUrl = (points) => {
    const fmt = (p) => `${p.lat.toFixed(6)},${p.lon.toFixed(6)}`;
    if (points.length === 1) {
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fmt(points[0]))}`;
    }
    const origin = points[0];
    const destination = points[points.length - 1];
    const MAX_WAYPOINTS = 8; // origin + destination + 8 => 10 stops, Google's practical limit
    const middle = points.slice(1, -1);
    let waypoints = middle;
    if (middle.length > MAX_WAYPOINTS) {
        waypoints = [];
        const step = (middle.length - 1) / (MAX_WAYPOINTS - 1);
        for (let i = 0; i < MAX_WAYPOINTS; i++) waypoints.push(middle[Math.round(i * step)]);
    }
    const params = new URLSearchParams();
    params.set('api', '1');
    params.set('origin', fmt(origin));
    params.set('destination', fmt(destination));
    if (waypoints.length > 0) params.set('waypoints', waypoints.map(fmt).join('|'));
    params.set('travelmode', 'walking');
    return `https://www.google.com/maps/dir/?${params.toString()}`;
};

const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.min(Math.floor(Math.log2(bytes) / 10), units.length - 1);
    const value = bytes / Math.pow(1024, i);
    return `${i === 0 ? value : value.toFixed(1)} ${units[i]}`;
};

// Case-insensitive metadata lookup (Azure/blob storage may normalise key casing,
// e.g. "startutc"). Shared by the row label and the metadata line.
const pickMeta = (md, key) => {
    if (!md) return undefined;
    if (md[key] !== undefined) return md[key];
    const lower = key.toLowerCase();
    const found = Object.keys(md).find((k) => k.toLowerCase() === lower);
    return found ? md[found] : undefined;
};

// Humanised start time used as the PRIMARY row label (e.g. "Wed, 19 Aug 2026, 08:34:00").
// The filename is machine-oriented and mostly duplicates this, so the start time leads.
const formatStartPrimary = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
        weekday: 'short', year: 'numeric', month: 'short', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
};

// Clock-only time (e.g. "08:39:12") for the compact stop timestamp on the secondary line.
const formatClockTime = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

// Recording length derived from the start/end UTC timestamps, as "1h 5m 12s".
const formatDuration = (startIso, endIso) => {
    if (!startIso || !endIso) return null;
    const start = new Date(startIso).getTime();
    const end = new Date(endIso).getTime();
    if (isNaN(start) || isNaN(end) || end < start) return null;
    let sec = Math.round((end - start) / 1000);
    const h = Math.floor(sec / 3600); sec -= h * 3600;
    const m = Math.floor(sec / 60); sec -= m * 60;
    const parts = [];
    if (h > 0) parts.push(`${h}h`);
    if (m > 0 || h > 0) parts.push(`${m}m`);
    parts.push(`${sec}s`);
    return parts.join(' ');
};

// Measured audio sample rate (stored in Hz, possibly fractional) shown in kHz.
const formatSampleRate = (sr) => {
    if (sr === undefined || sr === null || sr === '') return null;
    const hz = parseFloat(sr);
    if (!isFinite(hz) || hz <= 0) return null;
    return `${(hz / 1000).toFixed(3)} kHz`;
};

// Compact, muted SECONDARY line under a recording: stop time, sample rate, size,
// codec, and the de-emphasised raw blob filename (kept reachable but no longer the
// headline). The human-facing start time and duration are promoted to the primary
// row label (see the file row below). Full UTC values live in the tooltip.
const renderStreamMeta = (file) => {
    const md = (file && file.metadata) || {};
    const startUtc = pickMeta(md, 'startUtc');
    const endUtc = pickMeta(md, 'endUtc');
    const sampleRate = pickMeta(md, 'sampleRate');
    const state = pickMeta(md, 'state');

    const stop = formatClockTime(endUtc);
    const sr = formatSampleRate(sampleRate);
    const capsule = pickMeta(md, 'capsule');
    const size = (file && file.size !== undefined && file.size !== null) ? formatFileSize(file.size) : null;
    const codec = pickMeta(md, 'codec')
        || (file && file.name && file.name.includes('.') ? file.name.split('.').pop().toUpperCase() : null);

    const bits = [];
    if (stop) bits.push(`⏹ ${stop}`);
    else if (state === 'streaming' || state === 'uploading') bits.push('⏹ recording…');
    if (capsule) bits.push(`🎤 ${capsule}`);
    if (sr) bits.push(`🎵 ${sr}`);
    if (size) bits.push(`📦 ${size}`);
    if (codec) bits.push(`⚙️ ${codec}`);

    // Nothing worth showing and no filename to fall back on.
    if (bits.length === 0 && !(file && file.name)) return null;

    const tooltip = [
        startUtc ? `Start (UTC): ${startUtc}` : null,
        endUtc ? `End (UTC): ${endUtc}` : null,
        sampleRate ? `Sample rate: ${sampleRate} Hz` : null,
        file && file.name ? `File: ${file.name}` : null
    ].filter(Boolean).join('\n');

    return (
        <div
            title={tooltip || undefined}
            style={{
                fontSize: '0.72rem',
                color: '#9ca3af',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '2px 10px',
                marginLeft: '20px'
            }}
        >
            {bits.map((b, i) => <span key={i}>{b}</span>)}
            {file && file.name && (
                <span
                    style={{ fontFamily: 'monospace', color: '#cbd5e1', fontSize: '0.68rem' }}
                    title={file.name}
                >
                    {file.name}
                </span>
            )}
        </div>
    );
};

// Builds a self-describing JSON "sidecar" from a recording's blob metadata. The
// sidecar carries everything needed to place the audio on an absolute UTC
// timeline and to correct for clock drift: the start/end UTC, the measured
// sample rate, the exact captured sample count, and a snapshot of how well the
// recorder's clock was disciplined to UTC. The nominal rate / channels / bit
// depth are intentionally omitted because they already live in the audio file's
// own header. Optional fields that are absent are left out (JSON.stringify drops
// `undefined`), so the sidecar only ever states what is actually known.
const buildSidecar = (file, metadata) => {
    const md = metadata || {};
    // Blob storage may normalise metadata key casing, so match case-insensitively.
    const pick = (key) => {
        if (md[key] !== undefined) return md[key];
        const lower = key.toLowerCase();
        const found = Object.keys(md).find((k) => k.toLowerCase() === lower);
        return found ? md[found] : undefined;
    };
    const num = (v) => {
        const n = parseFloat(v);
        return isFinite(n) ? n : undefined;
    };

    const startUtc = pick('startUtc');
    const endUtc = pick('endUtc');
    const measuredRate = num(pick('sampleRate'));
    const sampleCount = num(pick('sampleCount'));
    const codec = pick('codec') || (file.name.includes('.') ? file.name.split('.').pop().toUpperCase() : undefined);

    // Duration: prefer the exact sampleCount / measured-rate (immune to endpoint
    // rounding), and fall back to the difference of the two UTC anchors.
    let durationSeconds;
    if (sampleCount !== undefined && measuredRate) {
        durationSeconds = sampleCount / measuredRate;
    } else if (startUtc && endUtc) {
        const d = (new Date(endUtc).getTime() - new Date(startUtc).getTime()) / 1000;
        if (isFinite(d) && d >= 0) durationSeconds = d;
    }

    const clockRef = pick('clockReference');
    const clockMaxError = num(pick('clockMaxError'));
    const clockSync = pick('clockSynchronised');
    const hasClock = clockRef !== undefined || clockMaxError !== undefined || clockSync !== undefined;

    return {
        chronosSidecar: '1.0',
        generatedUtc: new Date().toISOString(),
        file: {
            name: file.name,
            path: (file.path || '') + file.name,
            sizeBytes: (file.size !== undefined && file.size !== null) ? file.size : undefined
        },
        audio: {
            codec,
            measuredSampleRateHz: measuredRate,
            sampleCount
        },
        timing: {
            startUtc: startUtc || undefined,
            endUtc: endUtc || undefined,
            durationSeconds,
            // Linear model to recover the UTC of any sample n:
            //   utc(n) = sampleZeroUtc + n / sampleRateHz
            sampleToUtc: (startUtc && measuredRate) ? {
                model: 'linear',
                sampleZeroUtc: startUtc,
                sampleRateHz: measuredRate
            } : undefined
        },
        clock: hasClock ? {
            reference: clockRef,
            maxErrorSeconds: clockMaxError,
            synchronised: clockSync === undefined ? undefined : (clockSync === 'true' || clockSync === true)
        } : undefined,
        // Verbatim copy of every metadata key/value on the blob, for completeness.
        raw: md
    };
};


const FileExplorer =
    ({ files, deviceNameById, sharerNameById, onStreamedChunks, onStreamingStateChange, onAudioUrl, onRawAudioData, onFileSelected, selectedInfoPortal }) =>
    {
        const [expandedFolders, setExpandedFolders] = useState({});
        const [selectedFile, setSelectedFile] = useState(null); // Track selected file
        const [selectedMeta, setSelectedMeta] = useState(null);  // full meta of the selected file (for the waveform-tab header)
        const [contextMenu, setContextMenu] = useState(null); // { file, x, y }
        const contextMenuRef = useRef(null);
        // Measured position of the context menu; flips up/left when near a viewport edge.
        const [contextMenuPos, setContextMenuPos] = useState({ top: 0, left: 0, visibility: 'hidden' });

        // Metadata inspector modal (right-click -> Show metadata).
        const [metaModal, setMetaModal] = useState(null); // { file, metadata, loading, error }

        // Per-recording labels (a user-given name shown next to the duration).
        // Labels live in the "-comments.json" sidecar as a special comment with no
        // timeFrom, so they are fetched lazily per file and cached here by full
        // path. `labelFetchRef` remembers which paths were already requested so the
        // 2s file-list poll never re-hammers the server.
        const [labelsByPath, setLabelsByPath] = useState({}); // fullPath -> label text ('' = none)
        const labelFetchRef = useRef(new Set());

        // Free-text filter over the recording labels. When non-empty, only files
        // whose label contains this text (case-insensitive) are shown, along with
        // the folders leading to them.
        const [labelFilter, setLabelFilter] = useState('');

        const [downloadState, setDownloadState] = useState(
            {
                active: false,
                file: null,
                received: 0,
                total: 0,
                error: null
            }
        );

        // State for the "Save to disk" download (context menu)
        const [saveState, setSaveState] = useState(
            {
                active: false,
                fileName: null,
                received: 0,
                total: 0,
                error: null
            }
        );

        // Master byte store: EVERY downloaded byte, kept contiguously and never gated by
        // MSE. The waveform is fed from this raw download directly (so the FULL waveform
        // builds up while streaming), and the MSE pump reads a bounded window from here via
        // mseHeadRef -- decoupling download -> waveform from playback, and enabling seek to
        // any already-downloaded position.
        const fullBytesRef = useRef(new Uint8Array(0)); // backing store (capacity may exceed length)
        const fullLenRef = useRef(0);                   // number of valid bytes in fullBytesRef
        const mseHeadRef = useRef(0);                   // next byte offset to append to the SourceBuffer

        // fMP4 fragment index for seeking: one entry per moof, {byteOffset, startTime}.
        const fragmentsRef = useRef([]);
        const fragCursorRef = useRef(0);        // parse cursor into fullBytes for index building
        const mediaTimescaleRef = useRef(0);    // timescale (ticks/sec) from the init segment
        const initByteLenRef = useRef(0);       // length of the init segment (ftyp..moov/first moof)
        const initBytesRef = useRef(null);      // captured init segment bytes (for seek re-seed)
        const reseedGuardRef = useRef(false);   // true while a seek re-seed is in progress
        // Byte offset of the trailing 'mfra' (fragment random-access) box, if present.
        // mfra is index metadata, NOT a media segment: appending it to the SourceBuffer
        // makes MSE throw a decode error and end the MediaSource. We cap the MSE byte
        // stream here so only moof/mdat media is ever appended.
        const mediaEndByteRef = useRef(0);      // 0 = unknown (use full length)

        // MediaSource Extension state (for M4A files)
        const [mediaSource, setMediaSource] = useState(null);
        const [sourceBuffer, setSourceBuffer] = useState(null);
        const [audioUrl, setAudioUrl] = useState(null);
        const [isProcessing, setIsProcessing] = useState(false); // Prevent concurrent processing
        const [hasInitialized, setHasInitialized] = useState(false); // Track if we've sent init chunk
        
        const abortControllerRef = useRef(null);
        const mediaSourceRef = useRef(null);
        const sourceBufferRef = useRef(null);
        // Bumped whenever new bytes land in the master store or MSE becomes ready, to
        // (re)trigger the pump effect (replaces the old FIFO-state-change trigger).
        const [pumpTick, setPumpTick] = useState(0);

        // Update refs when state changes - do this synchronously
        useEffect(
            () =>
            {
                mediaSourceRef.current = mediaSource;
            },
            [mediaSource]
        );

        useEffect(
            () =>
            {
                sourceBufferRef.current = sourceBuffer;
            },
            [sourceBuffer]
        );

        // Use a ref for processing state to avoid stale closures
        const isProcessingRef = useRef(false);
        // Always-current pointer to the latest processM4aChunks, so imperative event
        // handlers (e.g. updateend after a quota eviction, or the quota back-off timer)
        // can re-kick the pump without a stale closure.
        const processM4aChunksRef = useRef(null);

        // Read a big-endian uint32 from a byte array.
        const readU32 = (buf, off) =>
            ((buf[off] << 24) | (buf[off + 1] << 16) | (buf[off + 2] << 8) | buf[off + 3]) >>> 0;

        // Grow the master store by newBytes and feed the waveform. Amortized O(n) via
        // capacity doubling (the old FIFO copied its whole buffer on every append/consume).
        // The waveform is fed here directly from the raw download so the FULL waveform
        // builds up as fast as bytes arrive, independent of playback / MSE consumption.
        const appendFullBytes = useCallback(
            (newBytes) =>
            {
                if (!newBytes || newBytes.length === 0)
                {
                    return;
                }
                const need = fullLenRef.current + newBytes.length;
                let buf = fullBytesRef.current;
                if (need > buf.length)
                {
                    let cap = buf.length > 0 ? buf.length : 256 * 1024;
                    while (cap < need)
                    {
                        cap *= 2;
                    }
                    const grown = new Uint8Array(cap);
                    grown.set(buf.subarray(0, fullLenRef.current), 0);
                    buf = grown;
                    fullBytesRef.current = buf;
                }
                buf.set(newBytes, fullLenRef.current);
                fullLenRef.current = need;

                if (onRawAudioData && newBytes.length > 0)
                {
                    onRawAudioData(newBytes);
                }
            },
            [onRawAudioData]
        );

        // Parse the init segment (once) to learn the media timescale, needed to convert a
        // fragment's baseMediaDecodeTime (tfdt) into seconds for seeking.
        const parseTimescaleFromInit = useCallback(
            (buf, initLen) =>
            {
                const findBox = (start, end, type) =>
                {
                    let o = start;
                    while (o + 8 <= end)
                    {
                        const size = readU32(buf, o);
                        const t = String.fromCharCode(buf[o + 4], buf[o + 5], buf[o + 6], buf[o + 7]);
                        if (size < 8 || o + size > end)
                        {
                            break;
                        }
                        if (t === type)
                        {
                            return { start: o, end: o + size };
                        }
                        o += size;
                    }
                    return null;
                };

                const moov = findBox(0, initLen, 'moov');
                if (!moov)
                {
                    return 0;
                }
                // Prefer the track media timescale (moov/trak/mdia/mdhd).
                const trak = findBox(moov.start + 8, moov.end, 'trak');
                if (trak)
                {
                    const mdia = findBox(trak.start + 8, trak.end, 'mdia');
                    if (mdia)
                    {
                        const mdhd = findBox(mdia.start + 8, mdia.end, 'mdhd');
                        if (mdhd)
                        {
                            const version = buf[mdhd.start + 8];
                            const base = mdhd.start + 12;
                            const tsOff = version === 1 ? base + 16 : base + 8;
                            return readU32(buf, tsOff);
                        }
                    }
                }
                // Fallback: movie timescale (moov/mvhd).
                const mvhd = findBox(moov.start + 8, moov.end, 'mvhd');
                if (mvhd)
                {
                    const version = buf[mvhd.start + 8];
                    const base = mvhd.start + 12;
                    const tsOff = version === 1 ? base + 16 : base + 8;
                    return readU32(buf, tsOff);
                }
                return 0;
            },
            []
        );

        // Incrementally index fMP4 fragments as bytes arrive: for every complete moof box,
        // record its byte offset and start time (tfdt baseMediaDecodeTime / timescale). Only
        // walks fully-available top-level boxes and stops at the first incomplete box,
        // resuming from fragCursorRef on the next call. Robust because it only locates box
        // boundaries + reads an explicit timestamp (unlike the old size-slicing parser).
        const buildFragmentIndex = useCallback(
            () =>
            {
                const buf = fullBytesRef.current;
                const total = fullLenRef.current;
                const ts = mediaTimescaleRef.current;
                if (ts <= 0 || initByteLenRef.current <= 0)
                {
                    return;
                }
                let o = fragCursorRef.current < initByteLenRef.current
                    ? initByteLenRef.current
                    : fragCursorRef.current;
                while (o + 8 <= total)
                {
                    const size = readU32(buf, o);
                    if (size < 8 || o + size > total)
                    {
                        break; // incomplete box; wait for more bytes
                    }
                    const type = String.fromCharCode(buf[o + 4], buf[o + 5], buf[o + 6], buf[o + 7]);
                    if (type === 'mfra')
                    {
                        // Trailing fragment-index metadata: media ends here. Record it so the
                        // MSE pump never forwards it (MSE cannot parse mfra and would error).
                        mediaEndByteRef.current = o;
                        break;
                    }
                    if (type === 'moof')
                    {
                        const moofEnd = o + size;
                        let startTime = null;
                        let p = o + 8;
                        while (p + 8 <= moofEnd)
                        {
                            const bSize = readU32(buf, p);
                            const bType = String.fromCharCode(buf[p + 4], buf[p + 5], buf[p + 6], buf[p + 7]);
                            if (bSize < 8 || p + bSize > moofEnd)
                            {
                                break;
                            }
                            if (bType === 'traf')
                            {
                                const trafEnd = p + bSize;
                                let q = p + 8;
                                while (q + 8 <= trafEnd)
                                {
                                    const cSize = readU32(buf, q);
                                    const cType = String.fromCharCode(buf[q + 4], buf[q + 5], buf[q + 6], buf[q + 7]);
                                    if (cSize < 8 || q + cSize > trafEnd)
                                    {
                                        break;
                                    }
                                    if (cType === 'tfdt')
                                    {
                                        const version = buf[q + 8];
                                        let baseTime;
                                        if (version === 1)
                                        {
                                            baseTime = readU32(buf, q + 12) * 4294967296 + readU32(buf, q + 16);
                                        }
                                        else
                                        {
                                            baseTime = readU32(buf, q + 12);
                                        }
                                        startTime = baseTime / ts;
                                        break;
                                    }
                                    q += cSize;
                                }
                            }
                            if (startTime !== null)
                            {
                                break;
                            }
                            p += bSize;
                        }
                        if (startTime !== null)
                        {
                            fragmentsRef.current.push({ byteOffset: o, startTime });
                        }
                    }
                    o += size;
                }
                fragCursorRef.current = o;
            },
            []
        );

        // Byte offset of the last indexed fragment whose startTime <= t (for seeking).
        const findByteOffsetForTime = useCallback(
            (t) =>
            {
                const frags = fragmentsRef.current;
                if (frags.length === 0)
                {
                    return initByteLenRef.current;
                }
                let lo = 0;
                let hi = frags.length - 1;
                let ans = 0;
                while (lo <= hi)
                {
                    const mid = (lo + hi) >> 1;
                    if (frags[mid].startTime <= t)
                    {
                        ans = mid;
                        lo = mid + 1;
                    }
                    else
                    {
                        hi = mid - 1;
                    }
                }
                return frags[ans].byteOffset;
            },
            []
        );

        // Keep MediaSource.duration in sync with how much media has been DOWNLOADED (from the
        // fragment index), not just what is currently buffered. This is what makes
        // seek-anywhere work: with an Infinity duration the element's `seekable` range is only
        // the small buffered window, so seeks outside it get clamped (cursor desyncs, far
        // jumps snap back). A finite duration sets seekable = [0, duration], so a click
        // anywhere in downloaded audio fires 'seeking' at the true target and the re-seed
        // engine fills it. It also makes audioEl.duration finite so the UI duration / playback
        // buttons appear reliably. Safe to call often; it no-ops unless the estimate grew.
        const syncMediaDuration = useCallback(
            () =>
            {
                const ms = mediaSourceRef.current;
                const sb = sourceBufferRef.current;
                if (!ms || ms.readyState !== 'open' || !sb || sb.updating)
                {
                    return;
                }
                const frags = fragmentsRef.current;
                if (frags.length < 2)
                {
                    return;
                }
                const firstStart = frags[0].startTime;
                const lastStart = frags[frags.length - 1].startTime;
                const avgGap = (lastStart - firstStart) / (frags.length - 1);
                let estDuration = lastStart + (avgGap > 0 ? avgGap : 0);
                const bufEnd = sb.buffered.length > 0
                    ? sb.buffered.end(sb.buffered.length - 1)
                    : 0;
                if (estDuration < bufEnd)
                {
                    estDuration = bufEnd;
                }
                const cur = ms.duration;
                if (isFinite(estDuration) && estDuration > 0
                    && (!isFinite(cur) || estDuration - cur > 0.25))
                {
                    try
                    {
                        ms.duration = estDuration;
                    }
                    catch (durErr)
                    {
                        // Setting duration can throw if a remove is pending; harmless, we
                        // retry on the next updateend / pump tick.
                    }
                }
            },
            []
        );

        // Produce the next chunk to append to the SourceBuffer, reading from the master
        // store at mseHeadRef. The init segment is sent first; afterwards we forward a
        // CONTIGUOUS fMP4 byte stream (bounded per append so the forward-buffer cap stays
        // effective -- one huge append could overshoot the cap by minutes).
        const extractM4aData = useCallback(
            () =>
            {
                const full = fullBytesRef.current;
                const total = fullLenRef.current;
                const head = mseHeadRef.current;
                const avail = total - head;
                if (avail <= 0)
                {
                    return { data: null, consumeBytes: 0 };
                }
                // Whether the init segment has been appended is tracked by a REF
                // (initBytesRef), set synchronously in the updateend handler the moment the
                // first append completes. We must NOT use the async React `hasInitialized`
                // state here: the pump re-kicks from updateend before the state re-render, so
                // a stale `hasInitialized === false` would run the init path a second time --
                // re-appending the init segment AND advancing the read cursor past the first
                // media fragment (baseMediaDecodeTime 0). That leaves the buffered range
                // starting after time 0, so the element stalls at readyState 1 and never plays.
                if (initBytesRef.current === null)
                {
                    // Find where the init segment ends: end of moov, or start of first moof.
                    let moovEndOffset = -1;
                    let currentOffset = 0;
                    while (currentOffset + 8 <= total)
                    {
                        const boxSize = readU32(full, currentOffset);
                        let boxType = '';
                        for (let i = 0; i < 4; i++)
                        {
                            const byte = full[currentOffset + 4 + i];
                            boxType += (byte >= 32 && byte <= 126) ? String.fromCharCode(byte) : '?';
                        }
                        if (boxSize <= 8 || currentOffset + boxSize > total)
                        {
                            break;
                        }
                        if (boxType === 'moov')
                        {
                            moovEndOffset = currentOffset + boxSize;
                            break;
                        }
                        else if (boxType === 'moof')
                        {
                            moovEndOffset = currentOffset;
                            break;
                        }
                        currentOffset += boxSize;
                    }

                    if (moovEndOffset > 0)
                    {
                        return { data: full.slice(0, moovEndOffset), consumeBytes: moovEndOffset, isInitChunk: true };
                    }
                    if (downloadState.active && total < 256 * 1024)
                    {
                        return { data: null, consumeBytes: 0 };
                    }
                    const initChunkSize = Math.min(total, 256 * 1024);
                    return { data: full.slice(0, initChunkSize), consumeBytes: initChunkSize, isInitChunk: true };
                }

                const MSE_APPEND_CHUNK = 512 * 1024;
                // Never forward the trailing mfra index box (or anything after it) to MSE.
                const mediaEnd = mediaEndByteRef.current > 0 ? mediaEndByteRef.current : total;
                const mediaAvail = mediaEnd - head;
                if (mediaAvail <= 0)
                {
                    return { data: null, consumeBytes: 0 };
                }
                const take = Math.min(mediaAvail, MSE_APPEND_CHUNK);
                return { data: full.slice(head, head + take), consumeBytes: take, isInitChunk: false };
            },
            [downloadState.active]
        );

        // Initialize MediaSource for streaming
        const initializeMediaSource =
            () =>
            {
                if (!('MediaSource' in window))
                {
                    console.error('MediaSource not supported');
                    return null;
                }

                // Clean up existing MediaSource if any
                if (mediaSourceRef.current || sourceBufferRef.current || audioUrl)
                {
                    //console.log('Cleaning up existing MediaSource before creating new one');
                    cleanupMediaSource(true);
                }

                const ms = new MediaSource();
                setMediaSource(ms);

                const url = URL.createObjectURL(ms);
                setAudioUrl(url);
            
                // Notify parent component about the audio URL
                if (onAudioUrl)
                {
                    onAudioUrl(url);
                }

                ms.addEventListener(
                    'sourceopen',
                    () =>
                    {
                        //console.log('=== MEDIASOURCE OPENED ===');
                        //console.log(`MediaSource readyState: ${ms.readyState}, downloadActive: ${downloadState.active}`);
                        try
                        {
                            // Detect codec from file extension or wait for data analysis
                            // For now, use file extension as a hint for codec selection
                            const fileExtension = downloadState.file?.name?.toLowerCase().split('.').pop() || '';
                            const isLikelyFlac = fileExtension === 'flac';
                            
                            //console.log(`📄 File extension: ${fileExtension}, likely FLAC: ${isLikelyFlac}`);
                            
                            // Try different MIME types based on detected codec
                            // Order matters - try most likely codec first
                            let mimeTypes = [];
                            
                            if (isLikelyFlac) {
                                // For FLAC files - try FLAC codec first
                                mimeTypes = [
                                    'audio/mp4; codecs="flac"',        // FLAC in MP4 container
                                    'audio/mp4; codecs="mp4a.40.2"',  // Fallback to AAC
                                    'audio/mp4',                       // Generic MP4
                                ];
                            } else {
                                // For M4A/AAC files - try AAC codecs first
                                mimeTypes = [
                                    'audio/mp4; codecs="mp4a.40.2"',  // AAC-LC (most common)
                                    'audio/mp4; codecs="mp4a.40.5"',  // HE-AAC
                                    'audio/mp4; codecs="mp4a.40.29"', // HE-AAC v2
                                    'audio/mp4',                       // Generic MP4 audio
                                    'audio/x-m4a',                     // M4A specific
                                    'audio/aac',                       // Raw AAC
                                    'audio/mp4; codecs="flac"',        // Try FLAC as fallback
                                    // WebM formats for legacy support
                                    'audio/webm; codecs="opus"',
                                    'audio/webm; codecs="vorbis"',
                                    'audio/webm'
                                ];
                            }

                            let sb = null;
                            let selectedMimeType = null;
                        
                            for (const mimeType of mimeTypes)
                            {
                                const isSupported = MediaSource.isTypeSupported(mimeType);
                                //console.log(`MIME type '${mimeType}' supported: ${isSupported}`);
                                
                                if (isSupported)
                                {
                                    try
                                    {
                                        sb = ms.addSourceBuffer(mimeType);
                                        selectedMimeType = mimeType;
                                        //console.log(`✅ Created SourceBuffer with MIME type: ${mimeType}`);
                                        break;
                                    }
                                    catch (e)
                                    {
                                        console.warn(`❌ Failed to create SourceBuffer for ${mimeType}:`, e);
                                    }
                                }
                            }

                            if (!sb)
                            {
                                console.error('Could not create SourceBuffer with any supported MIME type');
                                return;
                            }

                            // Check for potential compatibility issues
                            if (!selectedMimeType)
                            {
                                console.error('Browser does not support any MP4/M4A formats with MediaSource Extensions');
                                console.error('This browser may not support audio streaming');
                                return;
                            }
                            else
                            {
                                //console.log(`✅ Successfully created SourceBuffer with: ${selectedMimeType}`);
                            }

                            setSourceBuffer(sb);
                            //console.log(`📊 SourceBuffer state: updating=${sb.updating}, mode=${sb.mode}`);

                            // Enhanced SourceBuffer event handlers
                            sb.addEventListener(
                                'updatestart',
                                () =>
                                {
                                    //console.log('SourceBuffer update started');
                                }
                            );

                            sb.addEventListener(
                                'updateend',
                                () =>
                                {
                                    //console.log('=== SOURCEBUFFER UPDATE COMPLETED ===');
                            
                                    // Reset processing flags first (both ref and state)
                                    isProcessingRef.current = false;
                                    setIsProcessing(false);
                            
                                    // Advance the MSE read cursor past the bytes we just
                                    // appended (the master store keeps them for the waveform
                                    // and for seeking).
                                    if (sb._pendingConsumeBytes > 0)
                                    {
                                        mseHeadRef.current += sb._pendingConsumeBytes;
                                        sb._pendingConsumeBytes = 0;
                                    }

                                    // Capture the init segment exactly once (first append), and
                                    // learn the media timescale, so a seek can re-seed the
                                    // SourceBuffer from an arbitrary downloaded fragment.
                                    if (initBytesRef.current === null && mseHeadRef.current > 0)
                                    {
                                        initByteLenRef.current = mseHeadRef.current;
                                        initBytesRef.current = fullBytesRef.current.slice(0, mseHeadRef.current);
                                        mediaTimescaleRef.current = parseTimescaleFromInit(fullBytesRef.current, mseHeadRef.current);
                                        buildFragmentIndex();
                                    }

                                    // Keep MediaSource.duration in sync with how much media has
                                    // been downloaded so the element's seekable range covers ALL
                                    // downloaded audio (enabling seek-anywhere) and audioEl.duration
                                    // is finite (so the UI duration / playback buttons appear).
                                    syncMediaDuration();
                            
                                    // If this updateend is the completion of a quota eviction
                                    // (sourceBuffer.remove), retry the append that was rejected. Its
                                    // bytes are still in the FIFO (we never consumed them), so nothing
                                    // is lost and now there is free space.
                                    if (sb._retryAppendAfterEvict)
                                    {
                                        sb._retryAppendAfterEvict = false;
                                        queueMicrotask(
                                            () =>
                                            {
                                                if (mediaSourceRef.current?.readyState === 'open'
                                                    && sourceBufferRef.current && !sourceBufferRef.current.updating
                                                    && !isProcessingRef.current
                                                    && processM4aChunksRef.current)
                                                {
                                                    processM4aChunksRef.current();
                                                }
                                            }
                                        );
                                    }
                                    // PROACTIVE EVICTION: after a normal append, trim already-played
                                    // audio behind the playhead so the buffered window stays small and
                                    // never approaches the quota. remove() is async and fires another
                                    // updateend; after it, buffered.start has advanced so this guard
                                    // (removeEnd > start + 5) is false -> no eviction loop.
                                    else if (!sb.updating && sb.buffered.length > 0)
                                    {
                                        const audioEl = document.querySelector('audio');
                                        const playhead = (audioEl && isFinite(audioEl.currentTime)) ? audioEl.currentTime : 0;
                                        const KEEP_BEHIND_SECONDS = 20;
                                        const rangeStart = sb.buffered.start(0);
                                        const removeEnd = playhead - KEEP_BEHIND_SECONDS;
                                        if (removeEnd > rangeStart + 5)
                                        {
                                            try
                                            {
                                                sb.remove(rangeStart, removeEnd);
                                            }
                                            catch (evictErr)
                                            {
                                                console.warn('Proactive buffer eviction failed:', evictErr);
                                            }
                                        }
                                    }
                            
                                    // Mark initialization as complete after first successful append
                                    if (!hasInitialized)
                                    {
                                        //console.log('Setting hasInitialized to true after first append');
                                        setHasInitialized(true);
                                    }

                                    // Keep the pump flowing: if more downloaded bytes remain to be
                                    // appended, re-kick. The forward-buffer cap inside the pump pauses
                                    // it once enough is buffered ahead of the playhead, so this cannot
                                    // busy-spin. (Replaces the old FIFO-state-change trigger.)
                                    if (!sb._retryAppendAfterEvict && !sb.updating
                                        && mseHeadRef.current < (mediaEndByteRef.current > 0 ? mediaEndByteRef.current : fullLenRef.current))
                                    {
                                        queueMicrotask(
                                            () =>
                                            {
                                                if (mediaSourceRef.current?.readyState === 'open'
                                                    && sourceBufferRef.current && !sourceBufferRef.current.updating
                                                    && !isProcessingRef.current
                                                    && processM4aChunksRef.current)
                                                {
                                                    processM4aChunksRef.current();
                                                }
                                            }
                                        );
                                    }

                                    // Multiple buffered ranges are EXPECTED after a seek (we re-seed
                                    // the SourceBuffer at the target fragment, creating a new range).
                                    // They are not an error; the <audio> element plays whichever range
                                    // contains the playhead. No action needed.
                            
                                    // Buffered ranges after successful append
                                    if (sb.buffered.length > 0)
                                    {
                                        for (let i = 0; i < sb.buffered.length; i++)
                                        {
                                            const start = sb.buffered.start(i);
                                            const end = sb.buffered.end(i);
                                            //console.log(`Range ${i}: ${start.toFixed(3)}s - ${end.toFixed(3)}s (${(end-start).toFixed(3)}s duration)`);
                                        }
                                
                                        // Notify parent about successful streaming (for waveform updates)
                                        if (onStreamedChunks)
                                        {
                                            const totalDuration = sb.buffered.end(sb.buffered.length-1);
                                            onStreamedChunks(totalDuration);
                                        }
                                
                                        // Check MediaSource health after successful buffering
                                        //console.log(`✅ POST-APPEND HEALTH CHECK:`);
                                        //console.log(`MediaSource readyState: ${ms.readyState}`);
                                        //console.log(`SourceBuffer updating: ${sb.updating}`);
                                        //console.log(`Total buffered duration: ${sb.buffered.length > 0 ? sb.buffered.end(sb.buffered.length-1).toFixed(3) : 0}s`);
                                        
                                        // Check if this was the final fragment (mfra box)
                                        if (sb._isFinalFragment)
                                        {
                                            //console.log('🏁 Final fragment (mfra) processed - ending MediaSource stream immediately');
                                            sb._isFinalFragment = false; // Reset flag
                                            setTimeout(
                                                () =>
                                                {
                                                    if (ms.readyState === 'open')
                                                    {
                                                        try
                                                        {
                                                            ms.endOfStream();
                                                            //console.log('✅ MediaSource ended after final mfra fragment');
                                                        }
                                                        catch (e)
                                                        {
                                                            console.warn('Error ending MediaSource after mfra:', e);
                                                        }
                                                    }
                                                },
                                                50 // Very short delay
                                            );
                                        }
                                        else
                                        {
                                            // Regular end-of-stream check
                                            const currentDownloadState = downloadState;
                                            const mseMediaEnd = mediaEndByteRef.current > 0 ? mediaEndByteRef.current : fullLenRef.current;
                                            const mseBytesLeft = mseMediaEnd - mseHeadRef.current;
                                            if (!currentDownloadState.active && mseBytesLeft === 0)
                                            {
                                                //console.log('⚠️ Download complete and all bytes appended - scheduling endOfStream in 100ms');
                                                setTimeout(
                                                    () =>
                                                    {
                                                        if (ms.readyState === 'open')
                                                        {
                                                            try
                                                            {
                                                                //console.log('🛑 CALLING endOfStream() from updateend handler (100ms delay)');
                                                                ms.endOfStream();
                                                                //console.log('✅ MediaSource stream ended successfully - all fragments processed');
                                                            }
                                                            catch (e)
                                                            {
                                                                console.warn('Error ending MediaSource stream:', e);
                                                            }
                                                        }
                                                    },
                                                    100 // Small delay to ensure all operations complete
                                                ); 

                                            }
                                            else if (!currentDownloadState.active && mseBytesLeft === 0)
                                            {
                                                // Additional check for when download is complete but we haven't detected it yet
                                                //console.log('ℹ️ Download appears complete, checking if we should end stream...');
                                                setTimeout(
                                                    () =>
                                                    {
                                                        if (ms.readyState === 'open' && (fullLenRef.current - mseHeadRef.current) === 0 && !downloadState.active)
                                                        {
                                                            console.log('� CALLING endOfStream() from updateend handler (500ms confirmation)');
                                                            try
                                                            {
                                                                ms.endOfStream();
                                                                //console.log('MediaSource stream ended after confirmation');
                                                            }
                                                            catch (e)
                                                            {
                                                                console.warn('Error ending MediaSource stream on confirmation:', e);
                                                            }
                                                        }
                                                    },
                                                    500 // Longer delay for confirmation check
                                                );
                                            }
                                        }
                                    }
                                    else
                                    {
                                        // For initialization segments, no buffered ranges are created until fragments are added
                                        if (!hasInitialized)
                                        {
                                            //console.log('ℹ️ Initialization segment processed - buffered ranges will be created when fragments are added');
                                        }
                                        else
                                        {
                                            console.error('❌ CRITICAL: No buffered ranges created after fragment append');
                                            console.error('This indicates the fragment data is not compatible with the browser\'s MP4 demuxer');
                                            console.error('Possible causes:');
                                            console.error('1. M4A file uses codecs not supported by MediaSource Extensions');
                                            console.error('2. Fragment structure is incompatible with initialization segment');
                                            console.error('3. Corrupted or incomplete fragment data');
                                        }
                                
                                        // Enhanced M4A structure analysis (only for actual errors)
                                        if (hasInitialized && sb._pendingConsumeBytes > 0)
                                        {
                                            const dbgStart = mseHeadRef.current;
                                            const analyzedData = fullBytesRef.current.slice(dbgStart, Math.min(dbgStart + 4096, fullLenRef.current)); // Analyze more data
                                            console.error('=== DETAILED M4A STRUCTURE ANALYSIS ===');
                                    
                                            // Hex dump of first 128 bytes
                                            const hexData = Array.from(analyzedData.slice(0, 128))
                                                .map(b => '0x' + b.toString(16).padStart(2, '0'))
                                                .join(' ');
                                            console.error('First 128 bytes hex dump:', hexData);
                                    
                                            // Parse MP4 box structure
                                            let currentOffset = 0;
                                            const boxes = [];
                                    
                                            while (currentOffset < analyzedData.length - 8)
                                            {
                                                try
                                                {
                                                    // Ensure we have at least 8 bytes for box header
                                                    if (currentOffset + 8 > analyzedData.length)
                                                    {
                                                        break;
                                                    }
                                                    
                                                    // Read box size (first 4 bytes, big-endian)
                                                    const boxSize = (analyzedData[currentOffset] << 24) | 
                                                                (analyzedData[currentOffset + 1] << 16) | 
                                                                (analyzedData[currentOffset + 2] << 8) | 
                                                                analyzedData[currentOffset + 3];
                                                    
                                                    // Safely read box type
                                                    let boxType = '';
                                                    for (let i = 0; i < 4; i++)
                                                    {
                                                        const byte = analyzedData[currentOffset + 4 + i];
                                                        boxType += (byte >= 32 && byte <= 126) ? String.fromCharCode(byte) : '?';
                                                    }
                                                    
                                                    boxes.push(
                                                        {
                                                            type: boxType,
                                                            size: boxSize,
                                                            offset: currentOffset
                                                        }
                                                    );
                                            
                                                    console.error(`Box found: type='${boxType}', size=${boxSize}, offset=${currentOffset}`);
                                            
                                                    if (boxSize <= 8 || boxSize > analyzedData.length)
                                                    {
                                                        console.error(`Invalid box size ${boxSize}, stopping analysis`);
                                                        break;
                                                    }
                                            
                                                    // Special handling for large boxes that extend beyond our sample
                                                    if (currentOffset + boxSize > analyzedData.length)
                                                    {
                                                        console.error(`Box '${boxType}' extends beyond sample (size: ${boxSize}, remaining: ${analyzedData.length - currentOffset})`);
                                                        break;
                                                    }
                                            
                                                    currentOffset += boxSize;
                                                }
                                                catch (e)
                                                {
                                                    console.error('Error parsing box structure:', e);
                                                    break;
                                                }
                                            }
                                    
                                            // Check for required boxes
                                            const hasFtyp = boxes.some(box => box.type === 'ftyp');
                                            const hasMoov = boxes.some(box => box.type === 'moov');
                                            const hasMdat = boxes.some(box => box.type === 'mdat');
                                            const hasFree = boxes.some(box => box.type === 'free');
                                            
                                            console.error(`=== BOX SUMMARY ===`);
                                            console.error(`ftyp (file type): ${hasFtyp ? '✅' : '❌'}`);
                                            console.error(`moov (metadata): ${hasMoov ? '✅' : '❌'}`);
                                            console.error(`mdat (media data): ${hasMdat ? '✅' : '❌'}`);
                                            console.error(`free (padding): ${hasFree ? '✅' : '❌'}`);
                                            console.error(`Total boxes found: ${boxes.length}`);
                                    
                                            // Critical analysis for MSE compatibility
                                            if (!hasMoov)
                                            {
                                                console.error('🚨 STREAMING INCOMPATIBLE: moov box not found in initialization chunk');
                                                console.error('This M4A file has the moov box positioned after mdat (at end of file)');
                                                console.error('MediaSource Extensions requires moov box BEFORE mdat for streaming');
                                                console.error('SOLUTION: Re-encode M4A with "faststart" option to move moov to beginning');
                                            }
                                            else if (hasMdat && hasMoov)
                                            {
                                                const moovIndex = boxes.findIndex(box => box.type === 'moov');
                                                const mdatIndex = boxes.findIndex(box => box.type === 'mdat');
                                                if (mdatIndex < moovIndex)
                                                {
                                                    console.error('🚨 STREAMING INCOMPATIBLE: mdat box comes before moov box');
                                                    console.error('MediaSource Extensions requires moov box BEFORE mdat for streaming');
                                                }
                                                else
                                                {
                                                    console.error('📋 Box order looks correct for streaming (moov before mdat)');
                                                }
                                            }
                                    
                                            // Check codec compatibility
                                            if (hasFtyp)
                                            {
                                                const ftypBox = boxes.find(box => box.type === 'ftyp');
                                                if (ftypBox && ftypBox.offset + 16 < analyzedData.length)
                                                {
                                                    const brand = String.fromCharCode(
                                                        analyzedData[ftypBox.offset + 8],
                                                        analyzedData[ftypBox.offset + 9],
                                                        analyzedData[ftypBox.offset + 10],
                                                        analyzedData[ftypBox.offset + 11]
                                                    );
                                                    console.error(`M4A brand: '${brand}'`);
                                                    
                                                    // Check for compatible brands
                                                    const compatibleBrands = ['M4A ', 'mp41', 'mp42', 'isom'];
                                                    const isCompatible = compatibleBrands.includes(brand);
                                                    console.error(`Brand compatibility: ${isCompatible ? '✅' : '⚠️'} (${brand})`);
                                                }
                                            }
                                        }
                                    }
                            
                                    // Don't automatically process more chunks here to prevent loops
                                    // Let the useEffect handle it when FIFO changes
                                }
                            );

                            sb.addEventListener(
                                'error',
                                (e) =>
                                {
                                    console.error('=== SOURCEBUFFER ERROR EVENT ===');
                                    console.error('Event:', e);
                                    console.error('Event type:', e.type);
                                    console.error('Target:', e.target);
                                    console.error('MediaSource readyState:', ms.readyState);
                                    console.error('SourceBuffer updating:', sb.updating);
                                    console.error('SourceBuffer mode:', sb.mode);
                                    console.error('SourceBuffer buffered ranges:', sb.buffered.length);
                            
                                    // Enhanced error analysis
                                    console.error('=== DETAILED ERROR ANALYSIS ===');
                                    if (sb._pendingConsumeBytes > 0)
                                    {
                                        const dbgStart = mseHeadRef.current;
                                        const errorData = fullBytesRef.current.slice(dbgStart, Math.min(dbgStart + 128, fullLenRef.current));
                                        console.error('Data that caused error (first 128 bytes):');
                                        const hexData = Array.from(errorData)
                                            .map(b => '0x' + b.toString(16).padStart(2, '0'))
                                            .join(' ');
                                        console.error(hexData);
                                        
                                        // Parse the problematic box structure
                                        if (errorData.length >= 8)
                                        {
                                            const boxSize = (errorData[0] << 24) | (errorData[1] << 16) | (errorData[2] << 8) | errorData[3];
                                                
                                            // Safely read box type with bounds checking
                                            let boxType = '';
                                            for (let i = 0; i < 4; i++)
                                            {
                                                const byte = errorData[4 + i];
                                                boxType += (byte >= 32 && byte <= 126) ? String.fromCharCode(byte) : '?';
                                            }
                                                
                                            console.error(`Problematic box: type='${boxType}', size=${boxSize}`);
                                                
                                            // Check for common issues
                                            if (boxSize <= 0 || boxSize > 100000000)
                                            {
                                                console.error('❌ Invalid box size - data corruption or wrong format');
                                            }
                                            else if (boxType === 'moof' && !hasInitialized)
                                            {
                                                console.error('❌ Found moof box before initialization complete - fragmentation issue');
                                            }
                                            else if (!['ftyp', 'moov', 'moof', 'mdat', 'free'].includes(boxType))
                                            {
                                                console.error(`❌ Unknown/unsupported box type: '${boxType}'`);
                                            }
                                        }
                                    }
                            
                                    // Check if MediaSource ended unexpectedly
                                    if (ms.readyState === 'ended')
                                    {
                                        console.error('� MediaSource ended unexpectedly during SourceBuffer operation');
                                        console.error('This indicates the fragmented MP4 format is incompatible with this browser');
                                        console.error('');
                                        console.error('=== FRAGMENTED MP4 COMPATIBILITY DIAGNOSIS ===');
                                        console.error('Current file appears to use fragmentation that is incompatible with MediaSource Extensions');
                                        console.error('');
                                        console.error('RECOMMENDED SOLUTIONS:');
                                        console.error('1. Try adding default_base_moof flag:');
                                        console.error('   ffmpeg -i input.m4a -movflags frag_keyframe+empty_moov+default_base_moof -frag_duration 1000000 output.m4a');
                                        console.error('');
                                        console.error('2. Try faststart approach:');
                                        console.error('   ffmpeg -i input.m4a -c copy -movflags faststart+frag_keyframe output.m4a');
                                        console.error('');
                                        console.error('3. Try different fragment duration:');
                                        console.error('   ffmpeg -i input.m4a -movflags frag_keyframe+empty_moov -frag_duration 2000000 output.m4a');
                                        console.error('');
                                        console.error('4. Check browser compatibility - try Chrome/Firefox/Safari');
                                        console.error('');
                                        
                                        // Disable further recovery attempts
                                        console.error('🛑 Recovery disabled - manual re-encoding required');
                                    }
                                    
                                    // Reset processing flags on error (both ref and state)
                                    isProcessingRef.current = false;
                                    setIsProcessing(false);
                                    
                                    // Try to get more error details
                                    if (e.target && e.target.error)
                                    {
                                        console.error('Target error:', e.target.error);
                                    }
                                }
                            );

                            sb.addEventListener(
                                'abort',
                                (e) =>
                                {
                                    console.warn('SourceBuffer operation aborted:', e);
                                }
                            );

                        }
                        catch (error)
                        {
                            console.error('Error setting up SourceBuffer:', error);
                        }
                    }
                );

            ms.addEventListener(
                'error',
                (e) =>
                {
                    console.error('=== MEDIASOURCE ERROR ===');
                    console.error('MediaSource error event:', e);
                    console.error('MediaSource readyState:', ms.readyState);
                    console.error('This usually indicates the media data format is incompatible');
                }
            );

            ms.addEventListener(
                'sourceended',
                () =>
                {
                    //console.log('=== MEDIASOURCE ENDED ===');
                    //console.log('MediaSource readyState:', ms.readyState);
                    //console.log('This can be normal (end of stream) or indicate an error occurred');
                }
            );

            ms.addEventListener(
                'sourceclose',
                () =>
                {
                    //console.log('=== MEDIASOURCE CLOSED ===');
                    //console.log('MediaSource readyState:', ms.readyState);
                }
            );

            return ms;
        };

        // Process M4A chunks and append to SourceBuffer
        const processM4aChunks = useCallback(
            () =>
            {
                // Enhanced safety checks
                if (!mediaSource)
                {
                    //console.log('❌ No MediaSource available for processing');
                    return;
                }
                
                if (mediaSource.readyState !== 'open')
                {
                    //console.log(`❌ MediaSource not ready (readyState: ${mediaSource.readyState})`);
                    return;
                }
                
                if (!sourceBuffer)
                {
                    //console.log('❌ No SourceBuffer available for processing');
                    return;
                }
                
                if (sourceBuffer.updating)
                {
                    //console.log('⏸️ SourceBuffer is updating, skipping processing');
                    return;
                }

                // Use ref for atomic check-and-set to prevent race conditions
                if (isProcessingRef.current)
                {
                    //console.log('Already processing, skipping to prevent race condition');
                    return;
                }

                isProcessingRef.current = true;
                setIsProcessing(true);

                // FORWARD-BUFFER CAP: never buffer more than ~90s ahead of the playhead.
                // Streaming the whole 20-min file into one SourceBuffer overflows the
                // browser's quota (QuotaExceededError) before playback even starts. By
                // keeping only a bounded window buffered ahead we stay well under quota;
                // as playback advances (and old audio is evicted) the pump resumes.
                //
                // The cap is measured within the buffered range that CONTAINS the playhead
                // (not the last range). After a backward seek the old forward range still
                // ends far ahead, but the playhead sits in a new, short range that we DO need
                // to keep filling -- so we must not let a stale far-ahead range pause us.
                if (sourceBuffer.buffered.length > 0)
                {
                    const audioEl = document.querySelector('audio');
                    const playhead = (audioEl && isFinite(audioEl.currentTime)) ? audioEl.currentTime : 0;
                    const MAX_AHEAD_SECONDS = 90;
                    const ranges = sourceBuffer.buffered;
                    let aheadInPlayheadRange = -1;
                    for (let i = 0; i < ranges.length; i++)
                    {
                        if (playhead >= ranges.start(i) - 0.5 && playhead <= ranges.end(i) + 0.5)
                        {
                            aheadInPlayheadRange = ranges.end(i) - playhead;
                            break;
                        }
                    }
                    // Only pause if the playhead's own range already extends far enough ahead.
                    // If the playhead isn't inside any range yet (e.g. right after a seek), we
                    // must keep appending to cover it, so do NOT pause.
                    if (aheadInPlayheadRange > MAX_AHEAD_SECONDS)
                    {
                        // Enough buffered ahead. Release the pump WITHOUT advancing the cursor,
                        // then re-check shortly; playback drains the buffer and lets us resume.
                        isProcessingRef.current = false;
                        setIsProcessing(false);
                        setTimeout(
                            () =>
                            {
                                if (mediaSourceRef.current?.readyState === 'open'
                                    && sourceBufferRef.current && !sourceBufferRef.current.updating
                                    && !isProcessingRef.current
                                    && processM4aChunksRef.current)
                                {
                                    processM4aChunksRef.current();
                                }
                            },
                            1000
                        );
                        return;
                    }
                }

                const result = extractM4aData();
                if (result.data && result.data.length > 0)
                {
                    try
                    {
                        // Enhanced debugging before append
                        //console.log(`=== APPENDING M4A TO SOURCEBUFFER ===`);
                        //console.log(`Data length: ${result.data.length} bytes`);
                        //console.log(`MediaSource readyState: ${mediaSource?.readyState}`);
                        //console.log(`SourceBuffer updating: ${sourceBuffer.updating}`);
                        //console.log(`SourceBuffer mode: ${sourceBuffer.mode}`);
                        
                        // Final safety check - don't append if MediaSource has ended
                        if (mediaSource.readyState === 'ended')
                        {
                            console.error('Cannot append to SourceBuffer - MediaSource has ended');
                            isProcessingRef.current = false;
                            setIsProcessing(false);
                            return;
                        }
                    
                        // Check if data starts with M4A signature for first chunk
                        const startsWithM4a = result.data.length >= 8 && 
                                            result.data[4] === 0x66 && result.data[5] === 0x74 && 
                                            result.data[6] === 0x79 && result.data[7] === 0x70;
                        if (sourceBuffer.buffered.length === 0) // Only check for first chunk
                        {
                            //console.log(`First chunk starts with ftyp box: ${startsWithM4a}`);
                        }
                    
                        // Log first few bytes for debugging
                        const firstBytes = Array.from(result.data.slice(0, 16))
                            .map(b => '0x' + b.toString(16).padStart(2, '0'))
                            .join(' ');
                        //console.log(`First 16 bytes: ${firstBytes}`);
                    
                        // Check buffered ranges before append
                        if (sourceBuffer.buffered.length > 0)
                        {
                            //console.log(`Existing buffered ranges: ${sourceBuffer.buffered.length}`);
                            for (let i = 0; i < sourceBuffer.buffered.length; i++)
                            {
                                //console.log(`Range ${i}: ${sourceBuffer.buffered.start(i)} - ${sourceBuffer.buffered.end(i)}`);
                            }
                        }
                        else
                        {
                            //console.log('No existing buffered ranges');
                        }
                    
                        // Validate box structure for debugging
                        if (result.data.length < 8)
                        {
                            console.error(`Data too small for MP4 box: ${result.data.length} bytes`);
                            isProcessingRef.current = false;
                            setIsProcessing(false);
                            return;
                        }
                    
                        // For fragmented MP4, validate the structure (initialization segment or fragment)
                        const firstBoxSize = (result.data[0] << 24) | (result.data[1] << 16) | (result.data[2] << 8) | result.data[3];
                        const firstBoxType = String.fromCharCode(result.data[4], result.data[5], result.data[6], result.data[7]);
                    
                        if (result.isInitChunk)
                        {
                            //console.log(`✅ Initialization segment: starts with '${firstBoxType}' (${firstBoxSize} bytes), total ${result.data.length} bytes`);
                            // For initialization, we expect ftyp + moov (+ possibly free boxes)
                            if (firstBoxType !== 'ftyp')
                            {
                                console.warn(`⚠️ Unusual initialization segment - expected 'ftyp' but found '${firstBoxType}'`);
                            }
                        }
                        else
                        {
                            // Post-init we forward a CONTIGUOUS fMP4 byte stream, so an append
                            // may start mid-box (e.g. inside an mdat). That is expected and fine
                            // for MSE; do NOT warn about the leading box type here.
                            if (firstBoxType === 'mfra')
                            {
                                //console.log(`📋 End-of-file metadata: 'mfra' box contains fragment index for seeking`);
                                //console.log('🏁 This appears to be the final box - stream should end after this append');
                            }
                        }
                    
                        // A seek re-seeded the byte cursor to a new fragment boundary. Reset the
                        // segment parser first so this moof is parsed as a fresh media segment
                        // (otherwise it is misread as a continuation of the previous partial
                        // fragment and produces no buffered range -> playback hangs).
                        if (sourceBuffer._needAbort)
                        {
                            sourceBuffer._needAbort = false;
                            try
                            {
                                sourceBuffer.abort();
                            }
                            catch (abortErr)
                            {
                                console.warn('sourceBuffer.abort() failed:', abortErr);
                            }
                        }
                        sourceBuffer.appendBuffer(result.data.buffer);
                    
                        // Store the bytes to consume after successful append
                        sourceBuffer._pendingConsumeBytes = result.consumeBytes;
                    
                        // Mark initialization as complete if this was the first chunk
                        if (result.isInitChunk)
                        {
                            //console.log('Marking initialization chunk as sent');
                        }
                        else if (firstBoxType === 'mfra')
                        {
                            // Special handling for mfra (end-of-file) box
                            //console.log('🎯 mfra box detected - this should be the final append');
                            // Set a flag or trigger end-of-stream after this append completes
                            sourceBuffer._isFinalFragment = true;
                        }
                    }
                    catch (error)
                    {
                        // QuotaExceededError is an expected, self-healing condition (the buffer
                        // is momentarily full); handle it quietly below via eviction instead of
                        // dumping a multi-line stack that floods the console.
                        if (error.name !== 'QuotaExceededError')
                        {
                            console.error('=== SOURCEBUFFER M4A APPEND ERROR ===');
                            console.error('Error name:', error.name);
                            console.error('Error message:', error.message);
                            console.error('Error code:', error.code);
                            console.error('Full error:', error);
                            
                            // Additional context
                            console.error('MediaSource state:', mediaSource?.readyState);
                            console.error(
                                'SourceBuffer state:',
                                {
                                    updating: sourceBuffer?.updating,
                                    mode: sourceBuffer?.mode,
                                    timestampOffset: sourceBuffer?.timestampOffset
                                }
                            );
                        }
                    
                        // Check if it's a specific known error
                        if (error.name === 'QuotaExceededError')
                        {
                            // The SourceBuffer is full. We stream the WHOLE file into a single
                            // SourceBuffer, which exceeds the browser's per-buffer quota on long
                            // recordings. Free space by evicting already-played audio behind the
                            // playhead, then retry THIS append -- its bytes were NOT consumed from
                            // the FIFO, so no audio is lost. This turns the buffer into a sliding
                            // window that follows the playhead.
                            let evicting = false;
                            try
                            {
                                if (sourceBuffer && !sourceBuffer.updating && sourceBuffer.buffered.length > 0)
                                {
                                    const audioEl = document.querySelector('audio');
                                    const playhead = (audioEl && isFinite(audioEl.currentTime)) ? audioEl.currentTime : 0;
                                    const rangeStart = sourceBuffer.buffered.start(0);
                                    const KEEP_BEHIND_SECONDS = 10; // keep a little history behind the playhead
                                    const removeEnd = Math.min(playhead - KEEP_BEHIND_SECONDS, sourceBuffer.buffered.end(0));
                                    if (removeEnd > rangeStart + 1)
                                    {
                                        // Retry the append once eviction completes (see updateend handler)
                                        sourceBuffer._retryAppendAfterEvict = true;
                                        sourceBuffer.remove(rangeStart, removeEnd);
                                        evicting = true;
                                    }
                                }
                            }
                            catch (evictErr)
                            {
                                console.warn('Buffer eviction failed:', evictErr);
                            }

                            if (!evicting)
                            {
                                // Nothing to evict yet (playback has not advanced far enough past
                                // the buffer start). Back off and retry instead of tight-looping;
                                // as playback drains the buffer, space frees up and the append
                                // succeeds. If the user never plays, we simply stop buffering here,
                                // which is the desired behaviour (don't hoard the whole file).
                                setTimeout(
                                    () =>
                                    {
                                        if (mediaSourceRef.current?.readyState === 'open'
                                            && sourceBufferRef.current && !sourceBufferRef.current.updating
                                            && !isProcessingRef.current
                                            && processM4aChunksRef.current)
                                        {
                                            processM4aChunksRef.current();
                                        }
                                    },
                                    500
                                );
                            }
                        }
                        else if (error.name === 'InvalidStateError')
                        {
                            console.error('InvalidStateError: SourceBuffer or MediaSource in invalid state');
                        }
                        else if (error.name === 'TypeError')
                        {
                            console.error('TypeError: Invalid M4A buffer data format');
                        }
                    
                        // Reset processing flags on error
                        isProcessingRef.current = false;
                        setIsProcessing(false);
                    }
                }
                else
                {
                    // No data to process, reset flags
                    isProcessingRef.current = false;
                    setIsProcessing(false);
                }
            },
            [mediaSource, sourceBuffer, extractM4aData]
        );

        // Keep the imperative ref pointed at the latest processM4aChunks so event
        // handlers (updateend retry, quota back-off timer) never call a stale version.
        processM4aChunksRef.current = processM4aChunks;
        
        // Process bytes whenever new data lands in the master store (pumpTick bump) or MSE
        // becomes ready. This initializes the MediaSource on the first M4A bytes and kicks
        // the pump; from then on the pump re-kicks itself from the updateend handler.
        useEffect(
            () =>
            {
                const total = fullLenRef.current;
                if (total <= 0)
                {
                    return undefined;
                }

                // Update the media duration as more of the file downloads, even if the pump is
                // currently paused by the forward-buffer cap (so a fully-downloaded tail stays
                // seekable). Guards internally against an in-progress SourceBuffer update.
                syncMediaDuration();

                if (isProcessingRef.current)
                {
                    return undefined;
                }

                const kick =
                    () =>
                    {
                        if (isProcessingRef.current)
                        {
                            return;
                        }
                        const full = fullBytesRef.current;
                        const len = fullLenRef.current;
                        if (len === 0)
                        {
                            return;
                        }

                        // Initialize MediaSource on the first M4A bytes.
                        if (!mediaSource && len >= 12)
                        {
                            const hasM4aSignature = full[4] === 0x66 && full[5] === 0x74
                                && full[6] === 0x79 && full[7] === 0x70;
                            if (hasM4aSignature)
                            {
                                initializeMediaSource();
                            }
                            return; // wait for sourceopen -> next pumpTick kicks the pump
                        }

                        if (sourceBuffer && !sourceBuffer.updating && !isProcessingRef.current
                            && mediaSource && mediaSource.readyState === 'open')
                        {
                            processM4aChunks();
                        }
                    };

                // Microtasks are NOT throttled in background tabs, so the pump keeps running
                // and audio continues. Re-entry is guarded by isProcessingRef and
                // sourceBuffer.updating, so this cannot busy-spin.
                let cancelled = false;
                queueMicrotask(() => { if (!cancelled) kick(); });
                return () => { cancelled = true; };
            },
            [pumpTick, hasInitialized, mediaSource, sourceBuffer, processM4aChunks, syncMediaDuration]
        );

        // Keep the FIFO -> SourceBuffer pump alive across tab switches, and prebuffer ahead
        // while hidden. The normal pump is microtask-driven (not throttled), but a hidden tab
        // can defer React effect scheduling, letting the event chain go quiet. While hidden we
        // run a watchdog interval that re-kicks the pump; background tabs clamp timers to ~1s,
        // so it effectively fires ~once per second - enough to re-arm the (event-driven,
        // unthrottled) append chain so it keeps draining the FIFO and building a forward audio
        // buffer. On becoming visible we stop the watchdog and kick once to recover instantly.
        useEffect(
            () =>
            {
                const kickPump =
                    () =>
                    {
                        const ms = mediaSourceRef.current;
                        const sb = sourceBufferRef.current;
                        if (ms && sb && ms.readyState === 'open' && !sb.updating && !isProcessingRef.current)
                        {
                            processM4aChunks();
                        }
                    };

                let hiddenTicker = null;
                const stopTicker =
                    () =>
                    {
                        if (hiddenTicker !== null)
                        {
                            clearInterval(hiddenTicker);
                            hiddenTicker = null;
                        }
                    };

                const handleVisibility =
                    () =>
                    {
                        if (document.visibilityState === 'visible')
                        {
                            stopTicker();
                            kickPump();
                        }
                        else if (hiddenTicker === null)
                        {
                            hiddenTicker = setInterval(kickPump, 1000);
                        }
                    };

                document.addEventListener('visibilitychange', handleVisibility);
                // If we mount while already hidden, start prebuffering right away.
                if (document.visibilityState === 'hidden')
                {
                    hiddenTicker = setInterval(kickPump, 1000);
                }
                return () =>
                {
                    document.removeEventListener('visibilitychange', handleVisibility);
                    stopTicker();
                };
            },
            [processM4aChunks]
        );

        // SEEK ENGINE: let the user click anywhere on the waveform and play from there,
        // in EITHER direction, within the already-downloaded portion. The waveform click
        // handler sets audio.currentTime, which fires 'seeking'. If the target time is
        // already buffered (inside the sliding MSE window), the browser seeks natively and
        // we do nothing. If it is NOT buffered (evicted behind, or not yet appended ahead),
        // we re-seed the SourceBuffer: point the MSE read cursor at the nearest downloaded
        // fragment whose start time <= target, then kick the pump. fMP4 fragments carry
        // their own decode timestamps (tfdt), so the appended data lands at the right place
        // on the timeline and playback resumes at the requested position.
        useEffect(
            () =>
            {
                if (!audioUrl)
                {
                    return undefined;
                }

                let audioEl = null;

                const onSeeking =
                    () =>
                    {
                        const el = audioEl;
                        if (!el)
                        {
                            return;
                        }
                        const sb = sourceBufferRef.current;
                        const ms = mediaSourceRef.current;
                        if (!sb || !ms || ms.readyState !== 'open')
                        {
                            return;
                        }
                        if (reseedGuardRef.current)
                        {
                            return;
                        }
                        const t = el.currentTime;

                        // Already buffered? Native seek handles it (both directions in-window).
                        for (let i = 0; i < sb.buffered.length; i++)
                        {
                            if (t >= sb.buffered.start(i) - 0.25 && t <= sb.buffered.end(i) + 0.25)
                            {
                                return;
                            }
                        }

                        // Not buffered: re-seed from the nearest downloaded fragment <= t.
                        if (fragmentsRef.current.length === 0 || initBytesRef.current === null)
                        {
                            return;
                        }
                        const targetByte = findByteOffsetForTime(t);
                        if (targetByte < initByteLenRef.current || targetByte >= fullLenRef.current)
                        {
                            return;
                        }

                        // Move the cursor and kick the pump. We do NOT clear the existing
                        // buffered ranges: appending at a fragment boundary starts a new range
                        // at the correct timeline position, and the old (still-buffered) range
                        // remains available for seeking back into it.
                        //
                        // The previous fixed-size (512KB) append almost certainly ended mid
                        // fragment, so the MSE segment parser is stuck mid-segment. Jumping the
                        // byte cursor to a new moof and appending would be mis-parsed (the parser
                        // treats the new moof as continuation of the old mdat) and produce NO
                        // buffered range -> playback hangs at the seek point. abort() resets the
                        // parser to expect a fresh media segment; the re-seed always starts at a
                        // moof boundary, so the next append parses cleanly.
                        mseHeadRef.current = targetByte;
                        sb._needAbort = true;
                        if (sb.updating)
                        {
                            // An append is in flight from the OLD cursor. Discard its pending
                            // consume: otherwise updateend adds it to the just-repositioned cursor,
                            // pushing it off the target moof into mid-mdat -> the next append is
                            // parsed as garbage, the SourceBuffer errors, and the MediaSource ends.
                            sb._pendingConsumeBytes = 0;
                            return;
                        }
                        isProcessingRef.current = false;
                        if (processM4aChunksRef.current)
                        {
                            processM4aChunksRef.current();
                        }
                    };

                const tryAttach =
                    () =>
                    {
                        const el = document.querySelector(`audio[src="${audioUrl}"]`)
                            || Array.from(document.querySelectorAll('audio')).find(a => a.src === audioUrl || a.currentSrc === audioUrl)
                            || document.querySelector('audio');
                        if (el)
                        {
                            audioEl = el;
                            el.addEventListener('seeking', onSeeking);
                            return true;
                        }
                        return false;
                    };

                let iv = null;
                if (!tryAttach())
                {
                    iv = setInterval(
                        () =>
                        {
                            if (tryAttach())
                            {
                                clearInterval(iv);
                                iv = null;
                            }
                        },
                        200
                    );
                    // Give up looking after a few seconds.
                    setTimeout(
                        () =>
                        {
                            if (iv)
                            {
                                clearInterval(iv);
                                iv = null;
                            }
                        },
                        5000
                    );
                }

                return () =>
                {
                    if (iv)
                    {
                        clearInterval(iv);
                    }
                    if (audioEl)
                    {
                        audioEl.removeEventListener('seeking', onSeeking);
                    }
                };
            },
            [audioUrl, findByteOffsetForTime]
        );

        const handleFileClick =
            async (file) =>
            {
                console.log(`File clicked: ${file.name} - cleaning up previous state`);
                
                // STEP 1: Clean up any previous download/processing
                // Abort any previous download immediately
                if (abortControllerRef.current) {
                    console.log('Aborting previous download');
                    abortControllerRef.current.abort();
                    abortControllerRef.current = null;
                }
                
                // Stop any previous streaming state
                if (onStreamingStateChange) {
                    onStreamingStateChange(false);
                }
                
                // Clear download state
                setDownloadState({
                    active: false,
                    file: null,
                    received: 0,
                    total: 0,
                    error: null
                });
                
                // Force cleanup of previous MediaSource and audio
                if (mediaSourceRef.current || sourceBufferRef.current || audioUrl) {
                    console.log('Cleaning up previous MediaSource');
                    cleanupMediaSource(true);
                }
                
                // Clear the master byte store + all streaming cursors
                fullBytesRef.current = new Uint8Array(0);
                fullLenRef.current = 0;
                mseHeadRef.current = 0;
                fragmentsRef.current = [];
                fragCursorRef.current = 0;
                mediaEndByteRef.current = 0;
                mediaTimescaleRef.current = 0;
                initByteLenRef.current = 0;
                initBytesRef.current = null;
                reseedGuardRef.current = false;
                
                // Reset processing state
                setIsProcessing(false);
                setHasInitialized(false);
                
                // Reset parent components
                if (onRawAudioData) {
                    onRawAudioData({ reset: true });
                }
                if (onStreamedChunks) {
                    onStreamedChunks(0);
                }
                if (onAudioUrl) {
                    onAudioUrl(null);
                }
                
                // Small delay to ensure cleanup is complete
                await new Promise(resolve => setTimeout(resolve, 100));
                
                // STEP 2: Set the selected file for visual highlighting
                setSelectedFile(file.path + file.name);
                setSelectedMeta(file);
                
                // STEP 3: Notify parent component about file selection
                if (onFileSelected) {
                    onFileSelected(file.path + file.name);
                }
                
                // STEP 4: Start new download
                console.log(`Starting download for: ${file.name}`);
                await fetchFileInChunks(file);
            };

        const fetchFileInChunks =
            async (file) =>
                {
                    console.log(`Starting fresh download for: ${file.name}`);
                    
                    // Create new abort controller for this download
                    const controller = new AbortController();
                    abortControllerRef.current = controller;

                    // All files are now MP4 containers (FLAC files use FLAC codec in MP4)
                    const fileExtension = file.name.toLowerCase().split('.').pop();
                    console.log(`Processing ${file.name} as MP4 container (extension: ${fileExtension})`);

                    // Initialize download state and clear FIFO
                    setDownloadState(
                        {
                            active: true,
                            file,
                            received: 0,
                            total: file.size || 0,
                            error: null
                        }
                    );
                    
                    // Notify parent about streaming state change
                    if (onStreamingStateChange)
                    {
                        onStreamingStateChange(true);
                    }
                    
                    // Reset the master byte store + all streaming cursors for the new download
                    fullBytesRef.current = new Uint8Array(0);
                    fullLenRef.current = 0;
                    mseHeadRef.current = 0;
                    fragmentsRef.current = [];
                    fragCursorRef.current = 0;
                    mediaEndByteRef.current = 0;
                    mediaTimescaleRef.current = 0;
                    initByteLenRef.current = 0;
                    initBytesRef.current = null;
                    reseedGuardRef.current = false;
                    
                    // Reset initialization flag for new download
                    setHasInitialized(false);
                    
                    // Reset processing flags but don't cleanup MediaSource yet
                    // Let it be cleaned up naturally when the new one is created
                    isProcessingRef.current = false;
                    setIsProcessing(false);

                    try
                    {
                        let totalSize = file.size || 0;
                        let received = 0;

                        //console.log(`Starting download of ${file.name} (${totalSize} bytes)`);

                        // Streaming strategy (was RTT-bound, hence "streaming is slow but the
                        // right-click download is fast"):
                        //  - The old loop called fetchFileProperties AND fetched a tiny 128KB
                        //    chunk on EVERY iteration, strictly serialized. That is two HTTP
                        //    round-trips per 128KB, so throughput was ~128KB / (2*RTT) instead of
                        //    saturating bandwidth like the one-shot apiDownloadFile.
                        //  - Now: only re-poll properties when we've caught up to the known size
                        //    (to learn the new size / whether recording finished), request a LARGE
                        //    window in a single fetch, and stream its body via a ReadableStream
                        //    reader so bytes reach the master store (and waveform) as they arrive.
                        //
                        // DECOUPLED FROM PLAYBACK: every downloaded byte goes straight into the
                        // master store (fullBytesRef) and the waveform sink, with NO backpressure
                        // from the MSE pump. So the FULL waveform builds up as fast as the file
                        // downloads (and keeps growing for live files), independent of playback.
                        // The MSE pump reads a bounded window from the master store separately.
                        const STREAM_WINDOW = 8 * 1024 * 1024; // bytes requested per fetch
                        const FLUSH_AT = 256 * 1024;           // batch reader output before setState

                        while ((received < totalSize) || (file.metadata.state !== 'finished'))
                        {
                            if (controller.signal.aborted)
                            {
                                //console.log('Download aborted');
                                return;
                            }

                            // Only hit the properties endpoint when we need to know if more bytes
                            // exist (caught up, or size unknown). For a finished file this happens
                            // at most once.
                            if (received >= totalSize)
                            {
                                const props = await fetchFileProperties(file.path + file.name);
                                file.metadata = props.data.metadata;
                                file.size = props.data.size;
                                totalSize = file.size || 0;

                                if (received >= totalSize)
                                {
                                    if (file.metadata.state === 'finished')
                                    {
                                        break; // fully downloaded
                                    }
                                    // Still recording, no new bytes yet - wait a beat.
                                    await new Promise(resolve => setTimeout(resolve, 300));
                                    continue;
                                }
                            }

                            const windowSize = Math.min(STREAM_WINDOW, totalSize - received);
                            const response = await fetchFileChunk(
                                file.path + file.name,
                                received,
                                windowSize,
                                controller.signal
                            );

                            if (!response.ok)
                            {
                                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                            }

                            if (response.body && typeof response.body.getReader === 'function')
                            {
                                // Stream the body: read network-sized packets and coalesce them
                                // into >=256KB batches before touching React state, so we get full
                                // bandwidth without one re-render per packet.
                                const reader = response.body.getReader();
                                let pending = [];
                                let pendingBytes = 0;

                                const flush =
                                    () =>
                                    {
                                        if (pendingBytes === 0)
                                        {
                                            return;
                                        }
                                        const merged = new Uint8Array(pendingBytes);
                                        let offset = 0;
                                        for (const part of pending)
                                        {
                                            merged.set(part, offset);
                                            offset += part.length;
                                        }
                                        pending = [];
                                        pendingBytes = 0;
                                        appendFullBytes(merged);
                                        buildFragmentIndex();
                                        setPumpTick(t => t + 1);
                                        setDownloadState(prevState => ({
                                            ...prevState,
                                            received: received,
                                            total: totalSize
                                        }));
                                    };

                                while (true)
                                {
                                    const { done, value } = await reader.read();
                                    if (done)
                                    {
                                        break;
                                    }
                                    if (controller.signal.aborted)
                                    {
                                        try { await reader.cancel(); } catch (e) { /* ignore */ }
                                        return;
                                    }
                                    received += value.length;
                                    pending.push(value);
                                    pendingBytes += value.length;
                                    if (pendingBytes >= FLUSH_AT)
                                    {
                                        flush();
                                        // Yield to the event loop so the MSE pump / waveform decode
                                        // can run, but do NOT gate the download on MSE consumption
                                        // (that is what coupled the waveform to playback before).
                                        await new Promise(resolve => setTimeout(resolve, 0));
                                    }
                                }
                                flush();
                            }
                            else
                            {
                                // Fallback for environments without a streaming response body.
                                const arrayBuffer = await response.arrayBuffer();
                                const chunk = new Uint8Array(arrayBuffer);
                                received += chunk.length;
                                appendFullBytes(chunk);
                                buildFragmentIndex();
                                setPumpTick(t => t + 1);
                                setDownloadState(prevState => ({
                                    ...prevState,
                                    received: received,
                                    total: totalSize
                                }));
                            }
                        }

                        // Capture current MediaSource reference for this download
                        // This prevents race conditions if user clicks another file before setTimeout fires
                        const currentMediaSource = mediaSourceRef.current;
                        const currentFilename = file.name;
                        
                        // Update state with completed download (no need to store finalBuffer)
                        setDownloadState(
                            {
                                active: false,
                                file,
                                received,
                                total: totalSize,
                                error: null
                            }
                        );

                        //console.log(`Download completed! Total size: ${received} bytes`);
                        
                        // All files are now MP4 containers - notify streaming end immediately
                        if (onStreamingStateChange) {
                            onStreamingStateChange(false);
                        }
                        
                        // Trigger final processing and stream ending after a short delay
                        // IMPORTANT: Use captured reference to prevent ending wrong MediaSource
                        setTimeout(
                            () =>
                            {
                                // Verify this is still the same MediaSource (user didn't click another file)
                                if (currentMediaSource && currentMediaSource === mediaSourceRef.current && currentMediaSource.readyState === 'open')
                                {
                                    //console.log(`🏁 Download complete for ${currentFilename} - checking for final processing...`);
                                    // Give time for final fragments to process, then end stream
                                    setTimeout(
                                        () =>
                                        {
                                            // Double-check MediaSource is still the same and valid
                                            if (currentMediaSource === mediaSourceRef.current && 
                                                (fullLenRef.current - mseHeadRef.current) === 0 && 
                                                currentMediaSource.readyState === 'open')
                                            {
                                                //console.log(`Final check: ending MediaSource stream for ${currentFilename}`);
                                                try
                                                {
                                                    currentMediaSource.endOfStream();
                                                    //console.log(`MediaSource ended for ${currentFilename}`);
                                                }
                                                catch (e)
                                                {
                                                    console.warn(`Error ending MediaSource for ${currentFilename}:`, e);
                                                }
                                            }
                                            else
                                            {
                                                //console.log(`Still processing or file changed: mseLeft=${fullLenRef.current - mseHeadRef.current} bytes, same=${currentMediaSource === mediaSourceRef.current}`);
                                            }
                                        },
                                        1000 // Wait 1 second for final processing
                                    );
                                }
                                else
                                {
                                    //console.log(`MediaSource changed - skipping endOfStream for ${currentFilename}`);
                                }
                            }, // Small initial delay
                            200
                        ); 

                    }
                    catch (error)
                    {
                        console.error('Download error:', error);
                        setDownloadState(prevState => ({
                            ...prevState,
                            active: false,
                            error: error.message
                        }));
                        
                        // Notify parent about streaming state change on error
                        if (onStreamingStateChange) {
                            onStreamingStateChange(false);
                        }
                    }
                };

        // Cleanup MediaSource resources
        const cleanupMediaSource = useCallback((force = false) => {
            const currentMediaSource = mediaSourceRef.current;
            const currentSourceBuffer = sourceBufferRef.current;
            
            //console.log(`Cleaning up MediaSource resources (force: ${force}, downloadActive: ${downloadState.active})`);
            
            // Reset processing state (both ref and state)
            isProcessingRef.current = false;
            setIsProcessing(false);
            
            // Always clean up SourceBuffer
            if (currentSourceBuffer) {
                setSourceBuffer(null);
            }
            
            // Handle MediaSource cleanup - but DON'T call endOfStream during force cleanup
            // That causes the MediaSource to be unusable for the next file
            if (currentMediaSource && force) {
                try {
                    // Just remove event listeners, don't end the stream
                    // The stream will be naturally cleaned up when URL is revoked
                    //console.log('Cleaning up MediaSource event listeners');
                } catch (e) {
                    console.warn('Error cleaning MediaSource during cleanup:', e);
                }
            }
            
            // Clean up MediaSource URL and state
            if (audioUrl && force) {
                URL.revokeObjectURL(audioUrl);
                setAudioUrl(null);
            }
            
            if (force) {
                setMediaSource(null);
            }
        }, [downloadState.active, audioUrl]);

        // Function to manually abort the current download
        const abortDownload = useCallback(() => {
            console.log('User manually aborted download');
            
            // Abort the download
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
            
            // Stop streaming state
            if (onStreamingStateChange) {
                onStreamingStateChange(false);
            }
            
            // Clear download state
            setDownloadState({
                active: false,
                file: null,
                received: 0,
                total: 0,
                error: 'Download aborted by user'
            });
            
            // Cleanup MediaSource
            if (mediaSourceRef.current || sourceBufferRef.current || audioUrl) {
                cleanupMediaSource(true);
            }
            
            // Clear the master byte store + all streaming cursors
            fullBytesRef.current = new Uint8Array(0);
            fullLenRef.current = 0;
            mseHeadRef.current = 0;
            fragmentsRef.current = [];
            fragCursorRef.current = 0;
            mediaEndByteRef.current = 0;
            mediaTimescaleRef.current = 0;
            initByteLenRef.current = 0;
            initBytesRef.current = null;
            reseedGuardRef.current = false;
            
            // Reset processing state
            setIsProcessing(false);
            setHasInitialized(false);
        }, [onStreamingStateChange, audioUrl, cleanupMediaSource]);

        // Cleanup effect
        useEffect(() => {
            return () => {
                // Use refs for cleanup to avoid stale closures
                if (abortControllerRef.current) {
                    abortControllerRef.current.abort();
                }
                
                // Direct cleanup using refs instead of the callback
                const currentMediaSource = mediaSourceRef.current;
                const currentSourceBuffer = sourceBufferRef.current;
                
                //console.log('Component unmounting - forcing cleanup of all resources');
                
                // Reset processing state
                isProcessingRef.current = false;
                
                // Clean up MediaSource resources directly
                if (currentMediaSource && currentMediaSource.readyState === 'open') {
                    try {
                        //console.log('Force ending MediaSource stream on unmount');
                        currentMediaSource.endOfStream();
                    } catch (e) {
                        console.warn('Error ending MediaSource stream on unmount:', e);
                    }
                }
                
                // Note: We don't revoke audioUrl here since state setters won't work in cleanup
                // The browser will handle cleanup when the component unmounts
            };
        }, []); // Empty dependency - only run on mount/unmount

        const toggleFolder =
            (path) =>
                {
                    setExpandedFolders((prev) => ({
                        ...prev,
                        [path]: !prev[path],
                    }));
                };

        // Close context menu when clicking elsewhere
        useEffect(() => {
            const handler = () => setContextMenu(null);
            window.addEventListener('click', handler);
            return () => window.removeEventListener('click', handler);
        }, []);

        // Position the context menu, flipping it up/left when the click is near a
        // viewport edge so it never overflows off-screen (e.g. a long file list).
        useLayoutEffect(() => {
            if (!contextMenu) {
                setContextMenuPos({ top: 0, left: 0, visibility: 'hidden' });
                return;
            }
            const el = contextMenuRef.current;
            if (!el) return;
            const margin = 8;
            const { innerWidth, innerHeight } = window;
            const { width, height } = el.getBoundingClientRect();
            let top = contextMenu.y;
            let left = contextMenu.x;
            if (top + height + margin > innerHeight) {
                // Flip above the cursor; clamp if it would overflow the top.
                top = Math.max(margin, contextMenu.y - height);
            }
            if (left + width + margin > innerWidth) {
                left = Math.max(margin, innerWidth - width - margin);
            }
            setContextMenuPos({ top, left, visibility: 'visible' });
        }, [contextMenu]);

        const handleContextMenu = (e, file) => {
            e.preventDefault();
            e.stopPropagation();
            setContextMenu({ file, x: e.clientX, y: e.clientY });
        };

        // Lazily pull the label for every file. Each path is fetched at most once
        // (tracked in labelFetchRef) so the periodic file-list refresh doesn't
        // re-request them; editing a label updates the cache in place.
        useEffect(() => {
            if (!Array.isArray(files)) return;
            let cancelled = false;
            (async () => {
                for (const f of files) {
                    if (cancelled) return;
                    if (!f || f.type !== 'file') continue;
                    const fp = (f.path || '') + f.name;
                    if (labelFetchRef.current.has(fp)) continue;
                    const text = await getFileLabel(fp);
                    if (cancelled) return; // a newer files list arrived; let its run retry this path
                    // Mark as fetched only AFTER a successful read, so a cancelled
                    // in-flight fetch (e.g. during the initial file-list polling) is
                    // retried by the next run instead of being permanently skipped.
                    labelFetchRef.current.add(fp);
                    setLabelsByPath((prev) => ({ ...prev, [fp]: text || '' }));
                }
            })();
            return () => { cancelled = true; };
        }, [files]);

        // Prompt for and store a recording's label. Reads the current label as the
        // prompt default, writes via the comments sidecar, and updates the local
        // cache immediately so the row reflects the change without a reload.
        const handleSetLabel = async (file) => {
            setContextMenu(null);
            const fp = file.path + file.name;
            const current = labelsByPath[fp] || '';
            const next = window.prompt(`Label for this recording\n(leave empty to clear):`, current);
            if (next === null) return; // cancelled
            const trimmed = next.trim();
            try {
                await setFileLabel(fp, trimmed);
                labelFetchRef.current.add(fp);
                setLabelsByPath((prev) => ({ ...prev, [fp]: trimmed }));
            } catch (err) {
                console.error('Failed to set label:', err);
                alert(`Failed to set label: ${err.message}`);
            }
        };

        const handleDownloadFile = async (file) => {
            setContextMenu(null);
            const filePath = file.path + file.name;
            const total = file.size || 0;
            setSaveState({
                active: true,
                fileName: file.name,
                received: 0,
                total,
                error: null
            });
            try {
                // Pull the file in bounded range chunks so the service never has
                // to buffer the whole recording in RAM (a single whole-file GET of
                // a large file OOM-kills it). The chunks are collected into a Blob
                // for the browser's download, then saved via an anchor click.
                const response = total > 0
                    ? fetchFileStreamed(filePath, total)
                    : await fetchFileComplete(filePath);
                const reader = response.body.getReader();
                const chunks = [];
                let received = 0;
                for (;;) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    chunks.push(value);
                    received += value.length;
                    setSaveState(prev => ({ ...prev, received, total: total || received }));
                }
                const url = window.URL.createObjectURL(new Blob(chunks));
                const a = document.createElement('a');
                a.href = url;
                a.download = file.name;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                setSaveState(prev => ({ ...prev, active: false }));
                // Auto-hide the completed indicator after a short delay
                setTimeout(() => {
                    setSaveState({ active: false, fileName: null, received: 0, total: 0, error: null });
                }, 2000);
            } catch (err) {
                console.error('Download failed:', err);
                setSaveState(prev => ({ ...prev, active: false, error: err.message }));
            }
        };

        const handleDeleteFile = async (file) => {
            setContextMenu(null);
            if (!window.confirm(`Delete "${file.name}"?`)) return;
            const deletedPath = file.path + file.name;
            try {
                await apiDeleteFile(deletedPath);
                // Only tear down playback when the file being viewed is the one
                // deleted. Deleting any other file must leave the current
                // waveform, selection and location map untouched. Reusing the
                // same cleanup as a file switch prevents a stale MediaSource /
                // waveform from lingering (and being mis-drawn on the next list
                // refresh) after the selected recording is removed.
                if (selectedFile === deletedPath) {
                    if (abortControllerRef.current) {
                        abortControllerRef.current.abort();
                        abortControllerRef.current = null;
                    }
                    if (onStreamingStateChange) onStreamingStateChange(false);
                    setDownloadState({ active: false, file: null, received: 0, total: 0, error: null });
                    if (mediaSourceRef.current || sourceBufferRef.current || audioUrl) {
                        cleanupMediaSource(true);
                    }
                    fullBytesRef.current = new Uint8Array(0);
                    fullLenRef.current = 0;
                    mseHeadRef.current = 0;
                    fragmentsRef.current = [];
                    fragCursorRef.current = 0;
                    mediaEndByteRef.current = 0;
                    mediaTimescaleRef.current = 0;
                    initByteLenRef.current = 0;
                    initBytesRef.current = null;
                    reseedGuardRef.current = false;
                    setIsProcessing(false);
                    setHasInitialized(false);
                    if (onRawAudioData) onRawAudioData({ reset: true });
                    if (onStreamedChunks) onStreamedChunks(0);
                    if (onAudioUrl) onAudioUrl(null);
                    setSelectedFile(null);
                    setSelectedMeta(null);
                    if (onFileSelected) onFileSelected(null);
                }
            } catch (err) {
                console.error('Delete failed:', err);
                alert(`Failed to delete "${file.name}": ${err.message}`);
            }
        };

        // Share a single recording with another user (read-only). The blob path is
        // "<ownerId>/<deviceId>/<fileName>", so the device folder is the second path
        // segment; the grant stores that device id plus the file name.
        const handleShareFile = async (file) => {
            setContextMenu(null);
            const email = window.prompt(`Share "${file.name}" with which user?\nEnter their email address:`);
            if (!email || !email.trim()) return;
            try {
                const user = await findUserByEmail(email.trim());
                if (!user) {
                    alert(`No user found with email "${email.trim()}".`);
                    return;
                }
                const segments = (file.path || '').split('/').filter(Boolean);
                const deviceRecordId = segments[1];
                if (!deviceRecordId) {
                    alert('Could not determine which device this file belongs to.');
                    return;
                }
                const deviceName = (deviceNameById && deviceNameById[deviceRecordId]) || deviceRecordId;
                await createFileShare(deviceRecordId, file.name, user.id, { deviceName });
                alert(`Shared "${file.name}" with ${user.email || email.trim()}.`);
            } catch (err) {
                console.error('Share failed:', err);
                alert(`Failed to share "${file.name}": ${err.message}`);
            }
        };

        // Open the recording's breadcrumb track in Google Maps. Google Maps can't
        // draw an arbitrary GPS polyline via URL, so this opens a Directions route
        // through the (downsampled) breadcrumbs in a new tab; the embedded map in
        // the stream view remains the exact, non-road-snapped track.
        const handleOpenInGoogleMaps = async (file) => {
            setContextMenu(null);
            try {
                const res = await apiGetLocations(file.path + file.name);
                const text = (res && res.status === 200) ? res.data : '';
                const points = parseBreadcrumbs(text);
                if (points.length === 0) {
                    alert(`No location track is available for "${file.name}".`);
                    return;
                }
                window.open(buildGoogleMapsTrackUrl(points), '_blank', 'noopener,noreferrer');
            } catch (err) {
                const code = err?.response?.status;
                if (code === 404 || code === 204) {
                    alert(`No location track is available for "${file.name}".`);
                } else {
                    console.error('Open in Google Maps failed:', err);
                    alert(`Failed to load the location track for "${file.name}": ${err.message}`);
                }
            }
        };

        // Export a JSON sidecar built from the recording's blob metadata. Fetches the
        // freshest properties (falling back to whatever is already on the file), then
        // downloads "<file>.json" next to how the audio would be saved. This lets a
        // consumer keep the UTC anchor / drift info alongside the raw audio file.
        const handleDownloadSidecar = async (file) => {
            setContextMenu(null);
            try {
                let metadata = file.metadata || {};
                try {
                    const props = await fetchFileProperties(file.path + file.name);
                    if (props?.data?.metadata) metadata = props.data.metadata;
                } catch {
                    // Fall back to any metadata already attached to the file row.
                }

                const sidecar = buildSidecar(file, metadata);
                const json = JSON.stringify(sidecar, null, 2);
                const url = window.URL.createObjectURL(new Blob([json], { type: 'application/json' }));
                const a = document.createElement('a');
                a.href = url;
                a.download = `${file.name}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            } catch (err) {
                console.error('Sidecar export failed:', err);
                alert(`Failed to build sidecar for "${file.name}": ${err.message}`);
            }
        };

        // Show the recording's blob metadata in a read-only modal. Fetches the
        // freshest properties (falling back to whatever is already attached to the
        // file row) so the user can inspect start/end UTC, sample rate, clock sync,
        // etc. without downloading anything.
        const handleShowMetadata = async (file) => {
            setContextMenu(null);
            setMetaModal({ file, metadata: file.metadata || {}, loading: true, error: null });
            try {
                const props = await fetchFileProperties(file.path + file.name);
                const metadata = props?.data?.metadata || file.metadata || {};
                setMetaModal({ file, metadata, loading: false, error: null });
            } catch (err) {
                console.error('Failed to load metadata:', err);
                // Still show whatever the file row already carries.
                setMetaModal({ file, metadata: file.metadata || {}, loading: false, error: err.message });
            }
        };

        // Transcode a FLAC recording into a single BW64 (Broadcast Wave) file with
        // its start time embedded in the bext chunk, so the user can drop it into a
        // DAW and have it spot to the correct timeline position -- no sidecar and no
        // server-side conversion. Decoding + writing happen client-side and stream
        // to disk, so multi-hour files never have to fit in memory. Chromium only.
        //
        // When resampleToUtc is true, the decoded PCM is additionally streamed
        // through libsamplerate to correct the recorder's constant clock drift, so
        // the exported audio maps 1:1 to real UTC time at the nominal rate (tracks
        // from different devices then stay aligned for the whole take, not just at
        // their start). This is heavier CPU work, still fully client-side.
        const handleDownloadWav = async (file, resampleToUtc = false) => {
            setContextMenu(null);
            if (!isBw64ExportSupported()) {
                alert(`Timecoded WAV export is unavailable.\n\n${bw64UnsupportedReason()}`);
                return;
            }

            const filePath = file.path + file.name;

            // Ask for the destination FIRST, synchronously within the click
            // gesture: showSaveFilePicker requires a live user activation, which
            // any preceding await (metadata fetch, opening the stream) would
            // consume for a large file -> "Must be handling user gesture".
            let fileHandle;
            try {
                fileHandle = await pickBw64SaveFile(file.name);
            } catch (err) {
                if (err?.name === 'AbortError') return;
                console.error('WAV export failed:', err);
                alert(`Timecoded WAV export failed: ${err.message}`);
                return;
            }

            setSaveState({ active: true, fileName: `${file.name} → WAV${resampleToUtc ? ' (UTC-aligned)' : ''}`, received: 0, total: 100, error: null, mode: 'percent' });
            try {
                // Freshest metadata for the bext timing + embedded sidecar.
                let metadata = file.metadata || {};
                try {
                    const props = await fetchFileProperties(filePath);
                    if (props?.data?.metadata) metadata = props.data.metadata;
                } catch {
                    // Fall back to whatever the file row already carries.
                }

                // Stream the FLAC in bounded range chunks so the service never
                // buffers the whole recording (which OOM-kills it).
                const total = file.size || 0;
                const flacResponse = total > 0
                    ? fetchFileStreamed(filePath, total)
                    : await fetchFileComplete(filePath);
                if (!flacResponse.ok) throw new Error(`Server returned ${flacResponse.status}`);

                // Note the drift-correction in the embedded sidecar for traceability.
                const sidecar = buildSidecar(file, metadata);
                if (resampleToUtc && sidecar) {
                    sidecar.export = {
                        utcResampled: true,
                        method: 'libsamplerate SRC_SINC_BEST_QUALITY',
                        note: 'PCM resampled from the measured rate to the nominal rate so samples map 1:1 to UTC time.'
                    };
                }

                const result = await downloadFlacAsBw64({
                    flacResponse,
                    fileName: file.name,
                    metadata,
                    sidecar,
                    fileHandle,
                    resampleToUtc,
                    onProgress: (frac) => {
                        setSaveState(prev => ({ ...prev, received: Math.round(frac * 100), total: 100 }));
                    }
                });

                if (result?.cancelled) {
                    setSaveState({ active: false, fileName: null, received: 0, total: 0, error: null });
                    return;
                }
                setSaveState(prev => ({ ...prev, active: false }));
                setTimeout(() => {
                    setSaveState({ active: false, fileName: null, received: 0, total: 0, error: null });
                }, 2000);
            } catch (err) {
                console.error('WAV export failed:', err);
                setSaveState(prev => ({ ...prev, active: false, error: err.message }));
            }
        };

        const getFolderName =
            (path) =>
                {
                    const segments = path.split('/').filter(Boolean);
                    return segments[segments.length - 1] || '/';
                };

        // Resolve the first folder level (a userId) to the logged-in user's name
        // and the second level (a deviceId) to the device's name. Only the current
        // user's files are ever listed, so the top-level id always maps to them.
        // deviceNameById maps a blob-path deviceId (PocketBase device record id) to
        // the device's friendly name.
        const currentUserId = getAuthUserId();
        const currentUserName = getAuthUser();
        const deviceNames = deviceNameById || {};
        const sharerNames = sharerNameById || {};

        // Icon + label for a folder node, honouring the user/device hierarchy.
        // depth 0 -> user (👤), depth 1 -> device (🎤). A top-level id that is NOT
        // the current user belongs to someone who shared content with us, so it is
        // labelled with the sharer and flagged shared; its device folders inherit
        // the shared flag. Anything unresolved keeps the plain folder icon + name.
        const resolveFolder =
            (fullPath, depth, isExpanded) =>
                {
                    const rawName = getFolderName(fullPath);
                    const topSeg = fullPath.split('/').filter(Boolean)[0];
                    const isShared = topSeg && currentUserId && topSeg !== currentUserId;
                    if (depth === 0 && currentUserId && rawName === currentUserId && currentUserName)
                    {
                        return { icon: '👤', label: `${currentUserName} (Owner)` };
                    }
                    if (depth === 0 && isShared)
                    {
                        const sharer = sharerNames[rawName];
                        const label = (sharer && sharer.name) || rawName;
                        const email = (sharer && sharer.email) || '';
                        return { icon: '👥', label: `${label} (Owner)`, shared: true, tooltip: email };
                    }
                    if (depth === 1)
                    {
                        const dn = deviceNames[rawName];
                        if (dn && dn !== rawName)
                        {
                            return { icon: '🎤', label: `${dn} (Device)`, shared: isShared };
                        }
                        if (isShared)
                        {
                            return { icon: '🎤', label: 'unknown (Device)', shared: true };
                        }
                    }
                    return { icon: isExpanded ? '📂' : '📁', label: rawName, shared: isShared };
                };

        const buildTree =
            () =>
                {
                    const root = {};

                    files.forEach(
                        (item) =>
                            {
                                const path = item.path;
                                const segments = path.split('/').filter(Boolean);
                                let current = root;

                                segments.forEach( 
                                    (segment, index) =>
                                        {
                                            const isLast = index === segments.length - 1;
                                            const fullPath = segments.slice(0, index + 1).join('/') + '/';

                                            if (!current[segment])
                                            {
                                                current[segment] = {
                                                    __meta__: null,
                                                    __children__: {},
                                                    __fullPath__: fullPath,
                                                };
                                            }

                                            if (isLast && item.type === 'directory')
                                            {
                                                current[segment].__meta__ = item;
                                            }

                                            current = current[segment].__children__;
                                        }
                                    );

                                // Add files
                                if (item.type === 'file')
                                {
                                    const parentPath = item.path;
                                    const parentSegments = parentPath.split('/').filter(Boolean);
                                    const parent = parentSegments.reduce(
                                        (acc, seg) =>
                                            {
                                                if (!acc[seg])
                                                {
                                                    acc[seg] = {
                                                        __meta__: null,
                                                        __children__: {},
                                                        __fullPath__: parentSegments.slice(0, parentSegments.indexOf(seg) + 1).join('/') + '/',
                                                    };
                                                }
                                                return acc[seg].__children__;
                                            },
                                            root
                                        );

                                    parent[item.name] = {
                                        __meta__: item,
                                        __children__: {},
                                        __fullPath__: parentPath,
                                    };
                                }
                            }
                        );

                    return root;
                };

        const renderTree =
            (node, depth = 0) =>
                {
                    const q = labelFilter.trim().toLowerCase();
                    // A subtree is kept when it directly is, or contains, a file whose
                    // label matches the active filter. Folders with no matching
                    // descendant are pruned entirely.
                    const matchesFilter = (n) =>
                    {
                        if (n.__meta__?.type === 'file')
                        {
                            return (labelsByPath[n.__meta__.path + n.__meta__.name] || '')
                                .toLowerCase().includes(q);
                        }
                        return n.__children__
                            ? Object.values(n.__children__).some(matchesFilter)
                            : false;
                    };
                    return Object.entries(node).map(
                        ([key, value]) =>
                            {
                                const { __meta__, __children__, __fullPath__ } = value;
                                if (q && !matchesFilter(value))
                                {
                                    return null;
                                }
                                // A node is a folder if the backend listed it as a directory OR
                                // if it is a synthetic path segment that has children but no own
                                // directory entry (e.g. the owner-scoped container root, which the
                                // storage backend never lists as a blob of its own).
                                const hasChildren = __children__ && Object.keys(__children__).length > 0;
                                const isFolder = __meta__?.type === 'directory' || (!__meta__ && hasChildren);
                                const isExpanded = expandedFolders[__fullPath__];

                                if (isFolder)
                                {
                                    const { icon: folderIcon, label: folderLabel, shared: folderShared, tooltip: folderTooltip } = resolveFolder(__fullPath__, depth, isExpanded);
                                    return (
                                        <div key={__fullPath__} style={{ marginLeft: depth * 16 }}>
                                            <div
                                                onClick={() => toggleFolder(__fullPath__)}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                                title={folderTooltip || undefined}
                                                style={{ cursor: 'pointer', fontWeight: '500', padding: '5px 8px', borderRadius: '5px', color: '#374151', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.875rem', transition: 'background-color 0.15s ease' }}
                                            >
                                                {folderIcon} {folderLabel}
                                                {folderShared && (
                                                    <span style={{ marginLeft: '4px', fontSize: '0.66rem', fontWeight: '600', color: '#7c3aed', backgroundColor: '#f3e8ff', border: '1px solid #e9d5ff', borderRadius: '10px', padding: '0 7px', letterSpacing: '0.03em' }} title="Shared with you (read-only)">🔗 Shared</span>
                                                )}
                                            </div>
                                            {isExpanded && renderTree(__children__, depth + 1)}
                                        </div>
                                    );
                                }

                                if (__meta__?.type === 'file')
                                {
                                    const isSelected = selectedFile === __meta__.path + __meta__.name;
                                    const fullFilePath = __meta__.path + __meta__.name;
                                    const fileLabel = labelsByPath[fullFilePath] || '';
                                    
                                    // Use the same logic as the working progress bar below
                                    const isDownloading = downloadState.active && selectedFile === fullFilePath;
                                    const hasError = downloadState.error;
                                    
                                    // Calculate download progress percentage
                                    const downloadProgress = isDownloading && downloadState.total > 0 
                                        ? Math.round((downloadState.received / downloadState.total) * 100) 
                                        : 0;
                                    
                                    return (
                                        <div
                                            key={__meta__.name + __fullPath__}
                                            style={{ 
                                                marginLeft: depth * 16,
                                                cursor: 'pointer', 
                                                backgroundColor: isSelected ? 'rgba(212, 175, 55, 0.12)' : 'transparent',
                                                border: isSelected ? '1px solid rgba(212, 175, 55, 0.5)' : '1px solid transparent',
                                                borderRadius: '6px',
                                                padding: '5px 8px',
                                                transition: 'all 0.15s ease',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                fontSize: '0.875rem',
                                                color: '#374151'
                                            }}
                                            onClick={() => handleFileClick(__meta__)}
                                            onContextMenu={(e) => handleContextMenu(e, __meta__)}
                                            onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                                            onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
                                        >
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    {__meta__.metadata.state === 'streaming' && <span>🔴</span>}
                                                    {__meta__.metadata.state === 'uploading' && <span>⭕</span>}
                                                    {(__meta__.metadata.state === 'finished' || !__meta__.metadata.state) && <span>💽</span>}
                                                    <span style={{ fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }}>
                                                        {formatStartPrimary(pickMeta(__meta__.metadata, 'startUtc')) || __meta__.name}
                                                    </span>
                                                    {(() => {
                                                        const dur = formatDuration(
                                                            pickMeta(__meta__.metadata, 'startUtc'),
                                                            pickMeta(__meta__.metadata, 'endUtc')
                                                        );
                                                        const st = pickMeta(__meta__.metadata, 'state');
                                                        const label = dur || ((st === 'streaming' || st === 'uploading') ? 'recording…' : null);
                                                        return label ? (
                                                            <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '0 8px', whiteSpace: 'nowrap' }}>
                                                                {label}
                                                            </span>
                                                        ) : null;
                                                    })()}
                                                    {fileLabel && (
                                                        <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#92400e', backgroundColor: '#fef3c7', border: '1px solid #fde68a', borderRadius: '10px', padding: '0 8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '220px' }} title={fileLabel}>
                                                            🏷️ {fileLabel}
                                                        </span>
                                                    )}
                                                    {__meta__.shared && (
                                                        <span style={{ fontSize: '0.66rem', fontWeight: '600', color: '#7c3aed', backgroundColor: '#f3e8ff', border: '1px solid #e9d5ff', borderRadius: '10px', padding: '0 7px', letterSpacing: '0.03em' }} title="Shared with you (read-only)">🔗 Shared</span>
                                                    )}
                                                </div>
                                                {renderStreamMeta(__meta__)}
                                            </div>
                                            
                                            {/* Download Status Indicators */}
                                            {isDownloading && (
                                                <div style={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    gap: '4px',
                                                    fontSize: '12px',
                                                    color: '#2196F3'
                                                }}>
                                                    <span title="Downloading...">⬇️</span>
                                                    <div style={{
                                                        width: '50px',
                                                        height: '8px',
                                                        backgroundColor: '#e0e0e0',
                                                        borderRadius: '4px',
                                                        overflow: 'hidden',
                                                        border: '1px solid #ccc'
                                                    }}>
                                                        <div style={{
                                                            width: `${downloadProgress}%`,
                                                            height: '100%',
                                                            backgroundColor: '#2196F3',
                                                            transition: 'width 0.2s ease'
                                                        }}></div>
                                                    </div>
                                                    <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{downloadProgress}%</span>
                                                    
                                                    {/* Abort Button */}
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation(); // Prevent file click
                                                            abortDownload();
                                                        }}
                                                        title="Abort Download"
                                                        style={{
                                                            background: '#f44336',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '3px',
                                                            padding: '1px 4px',
                                                            fontSize: '10px',
                                                            cursor: 'pointer',
                                                            marginLeft: '2px',
                                                            minWidth: '16px',
                                                            height: '16px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            )}
                                            
                                            {isSelected && hasError && (
                                                <div style={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    gap: '4px',
                                                    fontSize: '12px',
                                                    color: '#f44336'
                                                }}>
                                                    <span title="Download Error">❌</span>
                                                </div>
                                            )}
                                            
                                            {isSelected && !isDownloading && !hasError && (
                                                <div style={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center',
                                                    fontSize: '12px',
                                                    color: '#4caf50'
                                                }}>
                                                    <span title="Selected">✅</span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                }

                                return null;
                            });
                        };

        const tree = buildTree();

        // Every folder path in the current tree, for the expand/collapse-all button.
        const collectFolderPaths = (node, acc = []) => {
            Object.values(node).forEach((v) => {
                const hasChildren = v.__children__ && Object.keys(v.__children__).length > 0;
                const isFolder = v.__meta__?.type === 'directory' || (!v.__meta__ && hasChildren);
                if (isFolder) {
                    acc.push(v.__fullPath__);
                    collectFolderPaths(v.__children__, acc);
                }
            });
            return acc;
        };
        const allFolderPaths = collectFolderPaths(tree);
        const allExpanded = allFolderPaths.length > 0 && allFolderPaths.every((p) => expandedFolders[p]);
        const toggleExpandAll = () => {
            if (allExpanded) {
                setExpandedFolders({});
            } else {
                const next = {};
                allFolderPaths.forEach((p) => { next[p] = true; });
                setExpandedFolders(next);
            }
        };

        // Live metadata for the selected file (from the current props, so state / duration /
        // download progress stay in sync with the list), rendered as a header card into the
        // Waveform tab via a portal.
        const liveSelectedMeta = selectedFile
            ? ((files || []).find((f) => f.type === 'file' && (f.path + f.name) === selectedFile) || selectedMeta)
            : null;

        const renderSelectedFileCard = () => {
            const meta = liveSelectedMeta;
            if (!meta) return null;
            const fullFilePath = meta.path + meta.name;
            const fileLabel = labelsByPath[fullFilePath] || '';
            const isDownloading = downloadState.active && selectedFile === fullFilePath;
            const hasError = downloadState.error;
            const downloadProgress = isDownloading && downloadState.total > 0
                ? Math.round((downloadState.received / downloadState.total) * 100)
                : 0;
            const dur = formatDuration(pickMeta(meta.metadata, 'startUtc'), pickMeta(meta.metadata, 'endUtc'));
            const st = pickMeta(meta.metadata, 'state');
            const durLabel = dur || ((st === 'streaming' || st === 'uploading') ? 'recording…' : null);
            return (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                    backgroundColor: 'rgba(212, 175, 55, 0.10)', border: '1px solid rgba(212, 175, 55, 0.5)',
                    borderRadius: '8px', padding: '10px 14px', marginBottom: '1rem', fontSize: '0.875rem', color: '#374151'
                }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0, flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            {meta.metadata?.state === 'streaming' && <span>🔴</span>}
                            {meta.metadata?.state === 'uploading' && <span>⭕</span>}
                            {(meta.metadata?.state === 'finished' || !meta.metadata?.state) && <span>💽</span>}
                            <span style={{ fontWeight: 600, color: '#111827' }}>
                                {formatStartPrimary(pickMeta(meta.metadata, 'startUtc')) || meta.name}
                            </span>
                            {durLabel && (
                                <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '0 8px', whiteSpace: 'nowrap' }}>
                                    {durLabel}
                                </span>
                            )}
                            {fileLabel && (
                                <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#92400e', backgroundColor: '#fef3c7', border: '1px solid #fde68a', borderRadius: '10px', padding: '0 8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '260px' }} title={fileLabel}>
                                    🏷️ {fileLabel}
                                </span>
                            )}
                            {meta.shared && (
                                <span style={{ fontSize: '0.66rem', fontWeight: '600', color: '#7c3aed', backgroundColor: '#f3e8ff', border: '1px solid #e9d5ff', borderRadius: '10px', padding: '0 7px', letterSpacing: '0.03em' }} title="Shared with you (read-only)">🔗 Shared</span>
                            )}
                        </div>
                        {renderStreamMeta(meta)}
                    </div>

                    {isDownloading && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#2196F3' }}>
                            <span title="Downloading...">⬇️</span>
                            <div style={{ width: '80px', height: '8px', backgroundColor: '#e0e0e0', borderRadius: '4px', overflow: 'hidden', border: '1px solid #ccc' }}>
                                <div style={{ width: `${downloadProgress}%`, height: '100%', backgroundColor: '#2196F3', transition: 'width 0.2s ease' }}></div>
                            </div>
                            <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{downloadProgress}%</span>
                            <button
                                onClick={(e) => { e.stopPropagation(); abortDownload(); }}
                                title="Abort Download"
                                style={{ background: '#f44336', color: 'white', border: 'none', borderRadius: '3px', padding: '1px 4px', fontSize: '10px', cursor: 'pointer', marginLeft: '2px', minWidth: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                ✕
                            </button>
                        </div>
                    )}
                    {hasError && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#f44336' }}>
                            <span title="Download Error">❌</span>
                        </div>
                    )}
                    {!isDownloading && !hasError && (
                        <div style={{ display: 'flex', alignItems: 'center', fontSize: '12px', color: '#4caf50' }}>
                            <span title="Selected">✅</span>
                        </div>
                    )}
                </div>
            );
        };


        return (
            <div>
                {selectedInfoPortal && liveSelectedMeta && createPortal(renderSelectedFileCard(), selectedInfoPortal)}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <h3 style={{ margin: 0, fontSize: '0.85rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b7280' }}>Recorded Streams</h3>
                    {allFolderPaths.length > 0 && (
                        <button
                            type="button"
                            onClick={toggleExpandAll}
                            title={allExpanded ? 'Collapse all folders' : 'Expand all folders'}
                            style={{ border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '0.72rem', color: '#6b7280', borderRadius: '6px', padding: '4px 8px', display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                            {allExpanded ? '⊟ Collapse all' : '⊞ Expand all'}
                        </button>
                    )}
                </div>

                <div style={{ position: 'relative', marginBottom: '12px' }}>
                    <span style={{ position: 'absolute', left: '9px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', pointerEvents: 'none' }}>🏷️</span>
                    <input
                        type="text"
                        value={labelFilter}
                        onChange={(e) => setLabelFilter(e.target.value)}
                        placeholder="Search…"
                        style={{ width: '100%', boxSizing: 'border-box', padding: '6px 26px 6px 28px', fontSize: '0.8rem', border: '1px solid #e5e7eb', borderRadius: '6px', outline: 'none', color: '#374151' }}
                    />
                    {labelFilter && (
                        <button
                            type="button"
                            onClick={() => setLabelFilter('')}
                            title="Clear filter"
                            style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '0.9rem', color: '#9ca3af', lineHeight: 1, padding: '2px' }}
                        >
                            ×
                        </button>
                    )}
                </div>

                {renderTree(tree)}

                {/* Context Menu */}
                {contextMenu && (
                    <div
                        ref={contextMenuRef}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            position: 'fixed',
                            top: contextMenuPos.top,
                            left: contextMenuPos.left,
                            visibility: contextMenuPos.visibility,
                            backgroundColor: '#fff',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            zIndex: 1000,
                            minWidth: '140px',
                            overflow: 'hidden'
                        }}
                    >
                        <div style={{ padding: '6px 10px', fontSize: '11px', color: '#999', borderBottom: '1px solid #eee', userSelect: 'none' }}>
                            {contextMenu.file.name}
                        </div>
                        <div
                            onClick={() => handleShowMetadata(contextMenu.file)}
                            style={{ padding: '8px 14px', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            title="Show the recording's stored metadata (start/end UTC, sample rate, clock sync…)"
                        >
                            ℹ️ Show metadata
                        </div>
                        <div
                            onClick={() => handleDownloadFile(contextMenu.file)}
                            style={{ padding: '8px 14px', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                            ⬇️ Download FLAC (original)
                        </div>
                        <div
                            onClick={() => handleDownloadSidecar(contextMenu.file)}
                            style={{ padding: '8px 14px', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                            ⬇️ Download Metadata (.json)
                        </div>
                        {contextMenu.file.name.toLowerCase().endsWith('.flac') && (
                            <div
                                onClick={() => handleDownloadWav(contextMenu.file)}
                                style={{ padding: '8px 14px', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                title="Broadcast Wave with embedded start timecode — drop straight into a DAW"
                            >
                                ⬇️ Download as WAV (BWF, timecoded)
                            </div>
                        )}
                        {contextMenu.file.name.toLowerCase().endsWith('.flac') && (
                            <div
                                onClick={() => handleDownloadWav(contextMenu.file, true)}
                                style={{ padding: '8px 14px', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                title="Like above, plus resample to UTC: corrects the recorder's clock drift so the audio stays aligned to real time across the whole take (heavier CPU, client-side)"
                            >
                                ⬇️ Download as WAV (BWF, timecoded, resynced)
                            </div>
                        )}
                        {contextMenu.file.name.toLowerCase().endsWith('.flac') && (
                            <div
                                onClick={() => handleOpenInGoogleMaps(contextMenu.file)}
                                style={{ padding: '8px 14px', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                title="Open the GPS breadcrumb track in Google Maps (road-snapped route; the stream view shows the exact track)"
                            >
                                🗺️ Open location in Google Maps
                            </div>
                        )}
                        <div
                            onClick={() => handleShareFile(contextMenu.file)}
                            style={{ padding: '8px 14px', cursor: 'pointer', fontSize: '13px', display: contextMenu.file.shared ? 'none' : 'flex', alignItems: 'center', gap: '8px' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            title="Grant another user read-only access to this recording"
                        >
                            🔗 Share with user…
                        </div>
                        <div
                            onClick={() => handleSetLabel(contextMenu.file)}
                            style={{ padding: '8px 14px', cursor: 'pointer', fontSize: '13px', display: contextMenu.file.shared ? 'none' : 'flex', alignItems: 'center', gap: '8px' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            title="Give this recording a name shown in the list"
                        >
                            🏷️ Set label…
                        </div>
                        <div
                            onClick={() => handleDeleteFile(contextMenu.file)}
                            style={{ padding: '8px 14px', cursor: 'pointer', fontSize: '13px', color: '#d32f2f', display: contextMenu.file.shared ? 'none' : 'flex', alignItems: 'center', gap: '8px' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fff5f5'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                            🗑️ Delete
                        </div>
                    </div>
                )}

                {/* Metadata inspector modal */}
                {metaModal && (() => {
                    const md = metaModal.metadata || {};
                    const startUtc = pickMeta(md, 'startUtc');
                    const endUtc = pickMeta(md, 'endUtc');
                    const sampleRate = pickMeta(md, 'sampleRate');
                    const sampleCount = pickMeta(md, 'sampleCount');
                    const codec = pickMeta(md, 'codec');
                    const state = pickMeta(md, 'state');
                    const clockRef = pickMeta(md, 'clockReference');
                    const clockMaxError = pickMeta(md, 'clockMaxError');
                    const clockSync = pickMeta(md, 'clockSynchronised');
                    const rate = parseFloat(sampleRate);
                    const count = parseFloat(sampleCount);
                    let duration = formatDuration(startUtc, endUtc);
                    if (!duration && isFinite(rate) && rate > 0 && isFinite(count) && count > 0) {
                        let sec = Math.round(count / rate);
                        const h = Math.floor(sec / 3600); sec -= h * 3600;
                        const m = Math.floor(sec / 60); sec -= m * 60;
                        const parts = [];
                        if (h > 0) parts.push(`${h}h`);
                        if (m > 0 || h > 0) parts.push(`${m}m`);
                        parts.push(`${sec}s`);
                        duration = parts.join(' ');
                    }
                    const rows = [
                        ['Start (UTC)', startUtc],
                        ['End (UTC)', endUtc],
                        ['Duration', duration],
                        ['Sample rate', formatSampleRate(sampleRate) || (sampleRate ? `${sampleRate} Hz` : null)],
                        ['Sample count', sampleCount],
                        ['Codec', codec],
                        ['State', state],
                        ['Clock reference', clockRef],
                        ['Clock max error', clockMaxError !== undefined && clockMaxError !== null && clockMaxError !== '' ? `${clockMaxError} s` : null],
                        ['Clock synchronised', clockSync === undefined ? null : String(clockSync)],
                        ['Size', (metaModal.file.size !== undefined && metaModal.file.size !== null) ? formatFileSize(metaModal.file.size) : null],
                    ].filter(([, v]) => v !== undefined && v !== null && v !== '');
                    const rawKeys = Object.keys(md).sort();
                    return (
                        <div
                            onClick={() => setMetaModal(null)}
                            style={{
                                position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.35)',
                                zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
                            }}
                        >
                            <div
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                    backgroundColor: '#fff', borderRadius: '10px', boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
                                    width: 'min(560px, 100%)', maxHeight: '80vh', overflow: 'auto'
                                }}
                            >
                                <div style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '14px 18px', borderBottom: '1px solid #eee', position: 'sticky', top: 0, background: '#fff'
                                }}>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>Metadata</div>
                                        <div style={{ fontSize: '12px', color: '#6b7280', fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {metaModal.file.name}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setMetaModal(null)}
                                        style={{ border: 'none', background: 'transparent', fontSize: '20px', lineHeight: 1, cursor: 'pointer', color: '#6b7280', padding: '4px 8px' }}
                                        title="Close"
                                    >×</button>
                                </div>
                                <div style={{ padding: '16px 18px' }}>
                                    {metaModal.loading && (
                                        <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '10px' }}>Loading latest metadata…</div>
                                    )}
                                    {metaModal.error && (
                                        <div style={{ fontSize: '12px', color: '#b45309', marginBottom: '10px' }}>
                                            Couldn’t refresh from server ({metaModal.error}); showing cached values.
                                        </div>
                                    )}
                                    {rows.length > 0 ? (
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                            <tbody>
                                                {rows.map(([k, v]) => (
                                                    <tr key={k}>
                                                        <td style={{ padding: '5px 10px 5px 0', color: '#6b7280', whiteSpace: 'nowrap', verticalAlign: 'top' }}>{k}</td>
                                                        <td style={{ padding: '5px 0', color: '#111827', fontFamily: 'monospace', wordBreak: 'break-word' }}>{String(v)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        !metaModal.loading && <div style={{ fontSize: '13px', color: '#6b7280' }}>No metadata available for this file.</div>
                                    )}

                                    {rawKeys.length > 0 && (
                                        <details style={{ marginTop: '14px' }}>
                                            <summary style={{ fontSize: '12px', color: '#6b7280', cursor: 'pointer', userSelect: 'none' }}>
                                                Raw metadata ({rawKeys.length} keys)
                                            </summary>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', marginTop: '8px' }}>
                                                <tbody>
                                                    {rawKeys.map((k) => (
                                                        <tr key={k}>
                                                            <td style={{ padding: '4px 10px 4px 0', color: '#9ca3af', whiteSpace: 'nowrap', verticalAlign: 'top', fontFamily: 'monospace' }}>{k}</td>
                                                            <td style={{ padding: '4px 0', color: '#374151', fontFamily: 'monospace', wordBreak: 'break-word' }}>{String(md[k])}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </details>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {/* Download-to-disk Progress Toast */}
                {(saveState.active || saveState.error) && (
                    <div style={{
                        position: 'fixed',
                        bottom: '20px',
                        right: '20px',
                        backgroundColor: '#fff',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                        padding: '12px 16px',
                        zIndex: 1100,
                        minWidth: '260px',
                        fontSize: '13px',
                        color: '#374151'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <span>{saveState.error ? '❌' : '⬇️'}</span>
                            <span style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {saveState.fileName}
                            </span>
                        </div>
                        {saveState.error ? (
                            <div style={{ color: '#d32f2f', fontSize: '12px' }}>
                                Download failed: {saveState.error}
                            </div>
                        ) : (
                            <>
                                <div style={{
                                    width: '100%',
                                    height: '8px',
                                    backgroundColor: '#e0e0e0',
                                    borderRadius: '4px',
                                    overflow: 'hidden'
                                }}>
                                    <div style={{
                                        width: saveState.total > 0
                                            ? `${Math.round((saveState.received / saveState.total) * 100)}%`
                                            : '100%',
                                        height: '100%',
                                        backgroundColor: '#2196F3',
                                        transition: 'width 0.2s ease',
                                        ...(saveState.total > 0 ? {} : { opacity: 0.6 })
                                    }}></div>
                                </div>
                                <div style={{ marginTop: '6px', fontSize: '12px', color: '#6b7280', display: 'flex', justifyContent: 'space-between' }}>
                                    <span>
                                        {saveState.mode === 'percent'
                                            ? ''
                                            : `${formatFileSize(saveState.received)}${saveState.total > 0 ? ` / ${formatFileSize(saveState.total)}` : ''}`}
                                    </span>
                                    {saveState.total > 0 && (
                                        <span style={{ fontWeight: 'bold' }}>
                                            {Math.round((saveState.received / saveState.total) * 100)}%
                                        </span>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Audio Player - Invisible */}
                {audioUrl && (
                    <div style={{ 
                        display: 'none', // Hidden but still functional
                        background: '#e3f2fd', 
                        padding: '10px',
                        margin: '10px 0', 
                        borderRadius: '4px',
                        border: '1px solid #2196F3'
                    }}>
                        <div style={{ marginBottom: '10px' }}>🎵 Audio Stream Ready</div>
                        <audio 
                            controls 
                            src={audioUrl}
                            style={{ width: '100%' }}
                            //onLoadStart={() => console.log('Audio loading started')}
                            //onCanPlay={() => console.log('Audio can start playing')}
                            //onError={(e) => console.error('Audio error:', e)}
                        >
                            Your browser does not support the audio element.
                        </audio>
                    </div>
                )}
               
            </div>
        );
    };

export default FileExplorer;
